import { getStartOfCurrentWeek } from "../home/scheduleDate";
import { getBusinessWeekStartDayIndex } from "../../../utilities/businessWeek";
import React from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import CancelIcon from "@mui/icons-material/Cancel";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import OutlinedInput from "@mui/material/OutlinedInput";
import FormControl from "@mui/material/FormControl";
import ListItemText from "@mui/material/ListItemText";
import Checkbox from "@mui/material/Checkbox";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { formatDate } from "../requests/utilities/helpers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import LoadingButton from "@mui/lab/LoadingButton";
import CheckIcon from "@mui/icons-material/Check";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import BarChartIcon from "@mui/icons-material/BarChart";
import StackedLineChartIcon from "@mui/icons-material/StackedLineChart";
import AlignHorizontalLeftIcon from "@mui/icons-material/AlignHorizontalLeft";
import TocIcon from "@mui/icons-material/Toc";
import {
  LineChart,
  BarChart,
  Bar,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
  ScatterChart,
  Scatter,
} from "recharts";

import {
  getRightworkAPI,
  postRightworkAPI,
} from "../requests/utilities/requests";
import {
  API_evaluate_labor_model_scenario,
  API_get_labor_test_results,
} from "../../../constants";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { reconcileEvaluationStoreIds } from "./laborModelEvaluationSelection";

const INTRADAY_PRIMARY_COLOR = "#6d28d9";
const INTRADAY_PUBLISHED_REFERENCE_COLOR = "#64748b";
const INTRADAY_PUBLISHED_REFERENCE_KEY = "published_labor_reference";
const INTRADAY_SANDBOX_REFERENCE_KEY = "sandbox_labor_reference";
const INTRADAY_SERVICE_COLOR = "#cbd5e1";
const INTRADAY_SUPPORT_COLOR = "#86efac";
const INTRADAY_STAFFING_COLOR = "#99f6e4";
const INTRADAY_DELTA_INCREASE_COLOR = "#fda4af";
const INTRADAY_DELTA_DECREASE_COLOR = "#93c5fd";
const INTRADAY_SELECTED_TARGET_KEY = "selected_forecast_target_labor";
const INTRADAY_DELTA_INCREASE_BASE_KEY = "sandbox_delta_increase_base";
const INTRADAY_DELTA_INCREASE_GAP_KEY = "sandbox_delta_increase_gap";
const INTRADAY_DELTA_DECREASE_BASE_KEY = "sandbox_delta_decrease_base";
const INTRADAY_DELTA_DECREASE_GAP_KEY = "sandbox_delta_decrease_gap";
const INTRADAY_LABOR_LEVEL_MULTIPLIER = 4;
const INTRADAY_DELTA_AREA_KEYS = new Set([
  INTRADAY_DELTA_INCREASE_BASE_KEY,
  INTRADAY_DELTA_INCREASE_GAP_KEY,
  INTRADAY_DELTA_DECREASE_BASE_KEY,
  INTRADAY_DELTA_DECREASE_GAP_KEY,
]);

function summarizeEvaluationResults(testResults, contributions = {}) {
  const totals = {
    newHours: 0,
    oldHours: 0,
    newCost: 0,
    oldCost: 0,
    comparableRows: 0,
  };

  const storeImpacts = Object.entries(testResults || {}).map(
    ([storeName, days]) => {
      let hoursDelta = 0;
      let costDelta = 0;
      (days || []).forEach((day) => {
        (day.labor_results || []).forEach((result) => {
          ["new_hours", "old_hours", "new_cost", "old_cost"].forEach(
            (field) => {
              const value = Number(result[field]);
              if (!Number.isNaN(value)) {
                const targetField = {
                  new_hours: "newHours",
                  old_hours: "oldHours",
                  new_cost: "newCost",
                  old_cost: "oldCost",
                }[field];
                totals[targetField] += value;
              }
            }
          );
          if (
            result.new_hours !== undefined ||
            result.old_hours !== undefined ||
            result.new_cost !== undefined ||
            result.old_cost !== undefined
          ) {
            totals.comparableRows += 1;
          }
          const newHours = Number(result.new_hours);
          const oldHours = Number(result.old_hours);
          const newCost = Number(result.new_cost);
          const oldCost = Number(result.old_cost);
          if (Number.isFinite(newHours) || Number.isFinite(oldHours)) {
            hoursDelta +=
              (Number.isFinite(newHours) ? newHours : 0) -
              (Number.isFinite(oldHours) ? oldHours : 0);
          }
          if (Number.isFinite(newCost) || Number.isFinite(oldCost)) {
            costDelta +=
              (Number.isFinite(newCost) ? newCost : 0) -
              (Number.isFinite(oldCost) ? oldCost : 0);
          }
        });
      });
      return { storeName, hoursDelta, costDelta };
    }
  );

  return {
    ...totals,
    hasComparableTotals: totals.comparableRows > 0,
    topStoreImpacts: storeImpacts
      .sort(
        (left, right) =>
          Math.abs(right.hoursDelta) - Math.abs(left.hoursDelta)
      )
      .slice(0, 3),
    topContributors: Object.values(contributions || {})
      .sort(
        (left, right) =>
          Math.abs(Number(right.hours || 0)) -
          Math.abs(Number(left.hours || 0))
      )
      .slice(0, 3),
  };
}

function LaborModelTestModal(props) {
  const workingModelLabel = props.scenarioName || "Sandbox scenario";
  const [selectedStores, setSelectedStores] = React.useState([props.homeStore]);
  const [selectedMetric, setSelectedMetric] = React.useState("hours");
  const [data, setData] = React.useState(null);
  const [laborStandardContributionData, setLaborStandardContributionData] =
    React.useState(null);
  const [calculationStatus, setCalculationStatus] = React.useState("idle");
  const [selectedVisualization, setSelectedVisualization] =
    React.useState("table");
  const [intradaySelection, setIntradaySelection] = React.useState(null);
  const [intradayForecastTargetMode, setIntradayForecastTargetMode] =
    React.useState("sandbox");
  const [showIntradayScheduledHours, setShowIntradayScheduledHours] =
    React.useState(false);
  const [runType, setRunType] = React.useState(
    "forecasted_sandbox_vs_published"
  );
  const [volumeMin, setVolumeMin] = React.useState(1000);
  const [volumeMax, setVolumeMax] = React.useState(5000);
  const [volumeIncrement, setVolumeIncrement] = React.useState(500);
  const evaluationRequestRef = React.useRef(0);
  const storeNamesRef = React.useRef(props.storeNames);
  storeNamesRef.current = props.storeNames;
  const availableStoreIdsKey = (props.storeNames || [])
    .map((store) => String(store.id))
    .join(",");
  const isVolumeSensitivity =
    runType === "volume_sensitivity_analysis";
  const evaluationSelectStyles = {
    backgroundColor: "white",
    borderRadius: "12px",
    height: "40px",
    fontSize: "14px",
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#e2e8f0",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "#cbd5e1",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#6d28d9",
    },
  };
  const evaluationDateStyles = {
    "& .MuiOutlinedInput-root": {
      height: "40px",
      backgroundColor: "white",
      borderRadius: "12px",
      fontSize: "14px",
      "& fieldset": {
        borderColor: "#e2e8f0",
      },
      "&:hover fieldset": {
        borderColor: "#cbd5e1",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#6d28d9",
      },
    },
  };
  const volumeFieldStyles = {
    backgroundColor: "white",
    borderRadius: "12px",
    minWidth: "140px",
    "& .MuiOutlinedInput-root": {
      "& fieldset": { borderColor: "#e2e8f0" },
      "&:hover fieldset": { borderColor: "#c4b5fd" },
      "&.Mui-focused fieldset": { borderColor: "#6d28d9" },
    },
  };

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "calc(100% - 32px)",
    maxWidth: 1440,
    height: "calc(100% - 32px)",
    overflow: calculationStatus === "idle" ? "hidden" : "auto",
    bgcolor: "#f8fafc",
    border: "2px solid #E2E8F0",
    borderRadius: "15px",
    boxShadow: 24,
  };

  // Handle store selection changes
  const handleStoreSelectionChange = (event) => {
    const {
      target: { value },
    } = event;

    if (value.includes("all")) {
      // If "Select All" is selected and all stores are already selected, deselect all options
      if (selectedStores.length === props.storeNames.length) {
        setSelectedStores([]);
      } else {
        // Otherwise, toggle all checkboxes
        setSelectedStores(props.storeNames.map((store) => store.id));
      }
      return;
    }
    setSelectedStores(value);
  };

  const handleSelectMetricChange = (event) => {
    setSelectedMetric(event.target.value);
  };

  const handleVolumeMinChange = (event) => {
    setVolumeMin(event.target.value);
  };

  const handleVolumeMaxChange = (event) => {
    setVolumeMax(event.target.value);
  };

  const handleVolumeIncrementChange = (event) => {
    setVolumeIncrement(event.target.value);
  };

  // Handle run type change
  const handleRunTypeChange = (event) => {
    const value = event.target.value;
    if (value === "forecasted_sandbox_vs_published") {
      setRunType("forecasted_sandbox_vs_published");
    } else if (value === "earned_sandbox_vs_actual") {
      setRunType("earned_sandbox_vs_actual");
    } else if (value === "volume_sensitivity_analysis") {
      setRunType("volume_sensitivity_analysis");
    }
    setCalculationStatus("idle");
    setData(null);
    setLaborStandardContributionData(null);
    setSelectedVisualization("table");
  };

  // Handler for the Calculate button
  const handleCalculateClick = async () => {
    const requestId = evaluationRequestRef.current + 1;
    evaluationRequestRef.current = requestId;
    setCalculationStatus("loading");
    // Prepare the payload
    const payload = {
      company_id: props.company_id,
      store_ids: selectedStores.join(","),
      date_start: formatDate(startDate),
      date_end: formatDate(endDate),
      run_type: runType,
    };
    if (props.scenarioUuid) {
      payload.scenario_uuid = props.scenarioUuid;
      payload.expected_revision = props.scenarioRevision;
    }
    if (isVolumeSensitivity) {
      payload.volume_min = parsedVolumeMin;
      payload.volume_max = parsedVolumeMax;
      payload.volume_increment = parsedVolumeIncrement;
    }

    try {
      // Replace `API_get_labor_test_results` with your actual endpoint constant
      const response = props.scenarioUuid
        ? await postRightworkAPI(API_evaluate_labor_model_scenario, payload)
        : await getRightworkAPI(API_get_labor_test_results, payload);
      if (evaluationRequestRef.current !== requestId) {
        return;
      }
      if (response.status === 200) {
        const data = response.data.test_results;
        setData(data);
        const labor_standard_contribution_data =
          response.data.labor_standard_contribution;
        setLaborStandardContributionData(labor_standard_contribution_data);
        setCalculationStatus("completed");
        props.onEvaluationComplete?.({
          scenarioUuid: props.scenarioUuid,
          revision: props.scenarioRevision,
          runType,
          storeCount: selectedStores.length,
          dateStart: formatDate(startDate),
          dateEnd: formatDate(endDate),
          completedAt: new Date().toISOString(),
          ...summarizeEvaluationResults(
            data,
            labor_standard_contribution_data
          ),
        });
        // Process your data here (e.g., update state to display results)
      } else {
        throw new Error("Failed to fetch test results");
      }
    } catch (error) {
      if (evaluationRequestRef.current !== requestId) {
        return;
      }
      console.error("Error fetching test results:", error);
      setCalculationStatus("idle");
      // Optionally use toast to show error message
      toast.error("Error fetching test results");
    }
  };

  React.useEffect(() => {
    evaluationRequestRef.current += 1;
    setSelectedStores((current) =>
      reconcileEvaluationStoreIds(
        current,
        storeNamesRef.current,
        props.homeStore
      )
    );
    setCalculationStatus("idle");
    setData(null);
    setLaborStandardContributionData(null);
    setSelectedVisualization("table");
    setIntradaySelection(null);
  }, [
    availableStoreIdsKey,
    props.company_id,
    props.homeStore,
    props.scenarioRevision,
    props.scenarioUuid,
  ]);

  React.useEffect(() => {
    if (calculationStatus !== "completed") {
      setIntradaySelection(null);
    }
  }, [calculationStatus]);

  React.useEffect(() => {
    setIntradayForecastTargetMode("sandbox");
    setShowIntradayScheduledHours(false);
  }, [intradaySelection, runType]);


  const businessWeekStartDayIndex = getBusinessWeekStartDayIndex(
    props.storeNames,
    props.homeStore
  );


  const [week_start_date] = React.useState(
    getStartOfCurrentWeek(businessWeekStartDayIndex)
  );

  function addDays(theDate, days) {
    var newDate = new Date(theDate);
    newDate.setDate(theDate.getDate() + days);
    return newDate;
  }

  const [startDate, setStartDate] = React.useState(week_start_date);
  const [endDate, setEndDate] = React.useState(addDays(week_start_date, 6));

  // Reset calculation status upon any input change
  React.useEffect(() => {
    setCalculationStatus("idle");
  }, [
    selectedStores,
    week_start_date,
    startDate,
    endDate,
    volumeMin,
    volumeMax,
    volumeIncrement,
  ]);

  //Department selector logic
  const departments = React.useMemo(
    () => (props.roleTypes || []).map((roleType) => roleType.role_type),
    [props.roleTypes]
  );
  const [selectedDepartments, setSelectedDepartments] =
    React.useState(departments);

  React.useEffect(() => {
    setSelectedDepartments((current) => {
      const availableDepartments = new Set(departments);
      const validSelections = current.filter((department) =>
        availableDepartments.has(department)
      );

      return validSelections.length > 0 ? validSelections : departments;
    });
  }, [departments]);

  const selectedFields = ["new_cost", "new_hours", "old_cost", "old_hours"];

  const parsedVolumeMin = Number(volumeMin);
  const parsedVolumeMax = Number(volumeMax);
  const parsedVolumeIncrement = Number(volumeIncrement);
  const isVolumeInputValid =
    !Number.isNaN(parsedVolumeMin) &&
    !Number.isNaN(parsedVolumeMax) &&
    !Number.isNaN(parsedVolumeIncrement) &&
    parsedVolumeIncrement > 0 &&
    parsedVolumeMax >= parsedVolumeMin;

  function calculateSum(data, selectedFields, selectedRoles) {
    const sums = {};

    selectedFields.forEach((field) => {
      sums[field] = 0;
    });

    data.forEach((obj) => {
      if (
        selectedRoles.includes(obj.role_type) ||
        selectedRoles.includes("All")
      ) {
        selectedFields.forEach((field) => {
          sums[field] += obj[field];
        });
      }
    });

    return sums;
  }

  function calculateNewTotals(data, selectedRoles) {
    const totals = {
      new_cost: 0,
      new_hours: 0,
    };

    data?.forEach((obj) => {
      if (
        selectedRoles.includes(obj.role_type) ||
        selectedRoles.includes("All")
      ) {
        totals.new_cost += obj.new_cost || 0;
        totals.new_hours += obj.new_hours || 0;
      }
    });

    return totals;
  }

  // Department selector styling
  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 0;
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 160,
      },
    },
  };

  const handleDepartmentSelectionChange = (event) => {
    const {
      target: { value },
    } = event;
    const value_arr = typeof value === "string" ? value.split(",") : value;
    if (value_arr.length > 0) {
      setSelectedDepartments(value_arr);
    }
  };

  const intradayHasData = React.useCallback((intradayLabor) => {
    if (!intradayLabor) {
      return false;
    }

    if (Array.isArray(intradayLabor.totals) && intradayLabor.totals.length > 0) {
      return true;
    }

    const roleEntries = intradayLabor.by_role_type || {};
    if (
      Object.values(roleEntries).some(
        (entries) => Array.isArray(entries) && entries.length > 0
      )
    ) {
      return true;
    }

    const breakdownBuckets = [
      intradayLabor.sandbox_breakdown,
      intradayLabor.published_breakdown,
    ];

    if (
      breakdownBuckets.some((bucket) =>
        ["service", "support", "staffing", "scheduled"].some((key) =>
          Array.isArray(bucket?.[key]) && bucket[key].length > 0
        )
      )
    ) {
      return true;
    }

    const breakdownByRoleBuckets = [
      intradayLabor.sandbox_breakdown_by_role_type,
      intradayLabor.published_breakdown_by_role_type,
    ];

    return breakdownByRoleBuckets.some((bucket) =>
      Object.values(bucket || {}).some((roleBreakdown) =>
        ["service", "support", "staffing", "scheduled"].some((key) =>
          Array.isArray(roleBreakdown?.[key]) && roleBreakdown[key].length > 0
        )
      )
    );
  }, []);

  const handleIntradaySelection = (
    storeName,
    dayName,
    intradayLabor,
    storeId
  ) => {
    if (!intradayHasData(intradayLabor)) {
      return;
    }

    setIntradaySelection({
      storeName,
      dayName,
      storeId,
      intradayLabor,
    });
  };

  const averageIntradayLabor = React.useCallback(
    (intradayEntries) => {
      const validEntries = (intradayEntries || []).filter((entry) =>
        intradayHasData(entry)
      );

      if (validEntries.length === 0) {
        return null;
      }

      const roundToFour = (value) =>
        Math.round((Number(value || 0) + Number.EPSILON) * 10000) / 10000;

      const totalsAccumulator = {};
      const totalsCounts = {};
      const roleAccumulator = {};
      const roleCounts = {};
      const breakdownAccumulator = {
        sandbox: { service: {}, support: {}, staffing: {}, scheduled: {} },
        published: { service: {}, support: {}, staffing: {}, scheduled: {} },
      };
      const breakdownCounts = {
        sandbox: { service: {}, support: {}, staffing: {}, scheduled: {} },
        published: { service: {}, support: {}, staffing: {}, scheduled: {} },
      };
      const breakdownByRoleAccumulator = {
        sandbox: {},
        published: {},
      };
      const breakdownByRoleCounts = {
        sandbox: {},
        published: {},
      };

      validEntries.forEach((labor) => {
        (labor.totals || []).forEach(({ qh_num, old_hours = 0, new_hours = 0 }) => {
          const qhKey = Number(qh_num);
          if (!totalsAccumulator[qhKey]) {
            totalsAccumulator[qhKey] = { old_hours: 0, new_hours: 0 };
            totalsCounts[qhKey] = 0;
          }
          totalsAccumulator[qhKey].old_hours += Number(old_hours) || 0;
          totalsAccumulator[qhKey].new_hours += Number(new_hours) || 0;
          totalsCounts[qhKey] += 1;
        });

        Object.entries(labor.by_role_type || {}).forEach(
          ([role, roleEntries]) => {
            roleEntries.forEach(
              ({ qh_num, old_hours = 0, new_hours = 0 }) => {
                const qhKey = Number(qh_num);
                if (!roleAccumulator[role]) {
                  roleAccumulator[role] = {};
                  roleCounts[role] = {};
                }
                if (!roleAccumulator[role][qhKey]) {
                  roleAccumulator[role][qhKey] = {
                    old_hours: 0,
                    new_hours: 0,
                  };
                  roleCounts[role][qhKey] = 0;
                }
                roleAccumulator[role][qhKey].old_hours +=
                  Number(old_hours) || 0;
                roleAccumulator[role][qhKey].new_hours +=
                  Number(new_hours) || 0;
                roleCounts[role][qhKey] += 1;
              }
            );
          }
        );

        const accumulateBreakdown = (bucketName, breakdown) => {
          if (!breakdown) {
            return;
          }

          ["service", "support", "staffing", "scheduled"].forEach((key) => {
            (breakdown[key] || []).forEach(({ qh_num, hours = 0 }) => {
              const qhKey = Number(qh_num);
              if (!breakdownAccumulator[bucketName][key][qhKey]) {
                breakdownAccumulator[bucketName][key][qhKey] = 0;
                breakdownCounts[bucketName][key][qhKey] = 0;
              }
              breakdownAccumulator[bucketName][key][qhKey] +=
                Number(hours) || 0;
              breakdownCounts[bucketName][key][qhKey] += 1;
            });
          });
        };

        const accumulateBreakdownByRole = (bucketName, breakdownByRoleType) => {
          Object.entries(breakdownByRoleType || {}).forEach(
            ([role, roleBreakdown]) => {
              if (!breakdownByRoleAccumulator[bucketName][role]) {
                breakdownByRoleAccumulator[bucketName][role] = {
                  service: {},
                  support: {},
                  staffing: {},
                  scheduled: {},
                };
                breakdownByRoleCounts[bucketName][role] = {
                  service: {},
                  support: {},
                  staffing: {},
                  scheduled: {},
                };
              }

              ["service", "support", "staffing", "scheduled"].forEach((key) => {
                (roleBreakdown?.[key] || []).forEach(({ qh_num, hours = 0 }) => {
                  const qhKey = Number(qh_num);
                  if (!breakdownByRoleAccumulator[bucketName][role][key][qhKey]) {
                    breakdownByRoleAccumulator[bucketName][role][key][qhKey] = 0;
                    breakdownByRoleCounts[bucketName][role][key][qhKey] = 0;
                  }
                  breakdownByRoleAccumulator[bucketName][role][key][qhKey] +=
                    Number(hours) || 0;
                  breakdownByRoleCounts[bucketName][role][key][qhKey] += 1;
                });
              });
            }
          );
        };

        accumulateBreakdown("sandbox", labor.sandbox_breakdown);
        accumulateBreakdown("published", labor.published_breakdown);
        accumulateBreakdownByRole(
          "sandbox",
          labor.sandbox_breakdown_by_role_type
        );
        accumulateBreakdownByRole(
          "published",
          labor.published_breakdown_by_role_type
        );
      });

      const totals = Object.keys(totalsAccumulator)
        .map((qhKey) => {
          const count = totalsCounts[qhKey] || validEntries.length;
          const values = totalsAccumulator[qhKey];
          return {
            qh_num: Number(qhKey),
            old_hours: roundToFour(values.old_hours / count),
            new_hours: roundToFour(values.new_hours / count),
          };
        })
        .sort((a, b) => a.qh_num - b.qh_num);

      const by_role_type = {};
      Object.entries(roleAccumulator).forEach(([role, qhMap]) => {
        by_role_type[role] = Object.keys(qhMap)
          .map((qhKey) => {
            const count = roleCounts[role][qhKey] || validEntries.length;
            const values = qhMap[qhKey];
            return {
              qh_num: Number(qhKey),
              old_hours: roundToFour(values.old_hours / count),
              new_hours: roundToFour(values.new_hours / count),
            };
          })
          .sort((a, b) => a.qh_num - b.qh_num);
      });

      const formatBreakdownSeries = (bucketName, key) =>
        Object.keys(breakdownAccumulator[bucketName][key])
          .map((qhKey) => {
            const count =
              breakdownCounts[bucketName][key][qhKey] || validEntries.length;
            const totalHours = breakdownAccumulator[bucketName][key][qhKey];
            return {
              qh_num: Number(qhKey),
              hours: roundToFour(totalHours / count),
            };
          })
          .sort((a, b) => a.qh_num - b.qh_num);

      const sandbox_breakdown = {
        service: formatBreakdownSeries("sandbox", "service"),
        support: formatBreakdownSeries("sandbox", "support"),
        staffing: formatBreakdownSeries("sandbox", "staffing"),
        scheduled: formatBreakdownSeries("sandbox", "scheduled"),
      };

      const published_breakdown = {
        service: formatBreakdownSeries("published", "service"),
        support: formatBreakdownSeries("published", "support"),
        staffing: formatBreakdownSeries("published", "staffing"),
        scheduled: formatBreakdownSeries("published", "scheduled"),
      };

      const formatBreakdownByRole = (bucketName) => {
        const payload = {};
        Object.entries(breakdownByRoleAccumulator[bucketName]).forEach(
          ([role, keyLookup]) => {
            payload[role] = {};
            ["service", "support", "staffing", "scheduled"].forEach((key) => {
              payload[role][key] = Object.keys(keyLookup[key] || {})
                .map((qhKey) => {
                  const count =
                    breakdownByRoleCounts[bucketName][role][key][qhKey] ||
                    validEntries.length;
                  const totalHours = keyLookup[key][qhKey];
                  return {
                    qh_num: Number(qhKey),
                    hours: roundToFour(totalHours / count),
                  };
                })
                .sort((a, b) => a.qh_num - b.qh_num);
            });
          }
        );
        return payload;
      };

      const sandbox_breakdown_by_role_type = formatBreakdownByRole("sandbox");
      const published_breakdown_by_role_type = formatBreakdownByRole("published");

      return {
        totals,
        by_role_type,
        sandbox_breakdown,
        published_breakdown,
        sandbox_breakdown_by_role_type,
        published_breakdown_by_role_type,
      };
    },
    [intradayHasData]
  );

  const storeIntradaySummaries = React.useMemo(() => {
    if (!data) {
      return {};
    }

    const summaries = {};
    Object.entries(data).forEach(([location, values]) => {
      summaries[location] = {
        storeId: values?.[0]?.store_id,
        weeklyAverage: averageIntradayLabor(
          values.map((dayValue) => dayValue?.intraday_labor || null)
        ),
      };
    });

    return summaries;
  }, [data, averageIntradayLabor]);

  const averageIntradayByDay = React.useMemo(() => {
    if (!data) {
      return Array(7).fill(null);
    }

    const results = [];
    for (let dayIndex = 0; dayIndex < 7; dayIndex += 1) {
      const entries = Object.values(data).map(
        (storeDays) => storeDays?.[dayIndex]?.intraday_labor || null
      );
      results[dayIndex] = averageIntradayLabor(entries);
    }

    return results;
  }, [data, averageIntradayLabor]);

  const averageWeeklyIntraday = React.useMemo(() => {
    const weeklyEntries = Object.values(storeIntradaySummaries)
      .map((summary) => summary.weeklyAverage)
      .filter((entry) => intradayHasData(entry));

    if (weeklyEntries.length === 0) {
      return null;
    }

    return averageIntradayLabor(weeklyEntries);
  }, [
    storeIntradaySummaries,
    averageIntradayLabor,
    intradayHasData,
  ]);

  const formatQuarterHourLabel = React.useCallback((qhNum) => {
    if (typeof qhNum !== "number" || Number.isNaN(qhNum)) {
      return "";
    }

    let hours = Math.floor(qhNum);
    let minutes = Math.round((qhNum - hours) * 60);

    if (minutes === 60) {
      hours += 1;
      minutes = 0;
    }

    if (hours >= 24) {
      hours -= 24;
    }

    const period = hours >= 12 ? "PM" : "AM";
    const displayHour = ((hours + 11) % 12) + 1;
    const displayMinutes = minutes.toString().padStart(2, "0");

    return `${displayHour}:${displayMinutes} ${period}`;
  }, []);

  const intradayDepartmentsLabel = React.useMemo(() => {
    if (!selectedDepartments || selectedDepartments.length === 0) {
      return "";
    }
    if (selectedDepartments.length === departments.length) {
      return "All departments";
    }
    return `Departments: ${selectedDepartments.join(", ")}`;
  }, [selectedDepartments, departments]);

  const toIntradayLaborLevel = React.useCallback(
    (hours) =>
      Math.round(
        (Number(hours) || 0) * INTRADAY_LABOR_LEVEL_MULTIPLIER * 100
      ) / 100,
    []
  );

  const buildBreakdownSeries = React.useCallback(
    (breakdown, breakdownByRoleType) => {
      if (!breakdown) {
        return [];
      }

      const shouldFilterByDepartment =
        breakdownByRoleType &&
        selectedDepartments &&
        selectedDepartments.length > 0;

      const getEntries = (key) => {
        if (!shouldFilterByDepartment) {
          return breakdown?.[key] || [];
        }
        const filtered = [];
        selectedDepartments.forEach((role) => {
          (breakdownByRoleType?.[role]?.[key] || []).forEach((entry) => {
            filtered.push(entry);
          });
        });
        if (filtered.length > 0) {
          return filtered;
        }
        return breakdown?.[key] || [];
      };

      const service = getEntries("service");
      const support = getEntries("support");
      const staffing = getEntries("staffing");
      const scheduled = getEntries("scheduled");
      const accumulator = {};

      const ensureEntry = (qhNum) => {
        const key = Number(qhNum);
        if (!accumulator[key]) {
          accumulator[key] = {
            service_hours: 0,
            support_hours: 0,
            staffing_hours: 0,
            scheduled_hours: 0,
          };
        }
        return accumulator[key];
      };

      service.forEach((entry) => {
        const record = ensureEntry(entry.qh_num);
        record.service_hours += Number(entry.hours || 0);
      });

      support.forEach((entry) => {
        const record = ensureEntry(entry.qh_num);
        record.support_hours += Number(entry.hours || 0);
      });

      staffing.forEach((entry) => {
        const record = ensureEntry(entry.qh_num);
        record.staffing_hours += Number(entry.hours || 0);
      });

      scheduled.forEach((entry) => {
        const record = ensureEntry(entry.qh_num);
        record.scheduled_hours += Number(entry.hours || 0);
      });

      return Object.keys(accumulator)
        .map((qhKey) => {
          const qhNum = Number(qhKey);
          const values = accumulator[qhKey];
          return {
            qh_num: qhNum,
            time: formatQuarterHourLabel(qhNum),
            service_hours: toIntradayLaborLevel(values.service_hours),
            support_hours: toIntradayLaborLevel(values.support_hours),
            staffing_hours: toIntradayLaborLevel(values.staffing_hours),
            scheduled_hours: toIntradayLaborLevel(values.scheduled_hours),
          };
        })
        .sort((a, b) => a.qh_num - b.qh_num);
    },
    [formatQuarterHourLabel, selectedDepartments, toIntradayLaborLevel]
  );

  const intradaySandboxAreaData = React.useMemo(() => {
    if (!intradaySelection?.intradayLabor) {
      return [];
    }
    return buildBreakdownSeries(
      intradaySelection.intradayLabor.sandbox_breakdown,
      intradaySelection.intradayLabor.sandbox_breakdown_by_role_type
    );
  }, [intradaySelection, buildBreakdownSeries]);

  const intradayPublishedAreaData = React.useMemo(() => {
    if (!intradaySelection?.intradayLabor) {
      return [];
    }
    return buildBreakdownSeries(
      intradaySelection.intradayLabor.published_breakdown,
      intradaySelection.intradayLabor.published_breakdown_by_role_type
    );
  }, [intradaySelection, buildBreakdownSeries]);

  const intradayActualBreakdownData = React.useMemo(() => {
    if (!intradaySelection?.intradayLabor) {
      return [];
    }

    const breakdown = intradaySelection.intradayLabor.sandbox_breakdown;
    const breakdownByRoleType =
      intradaySelection.intradayLabor.sandbox_breakdown_by_role_type || {};
    if (!breakdown) {
      return [];
    }

    const shouldFilterByDepartment =
      selectedDepartments &&
      selectedDepartments.length > 0;

    const getEntries = (key) => {
      if (!shouldFilterByDepartment) {
        return breakdown?.[key] || [];
      }
      const filtered = [];
      selectedDepartments.forEach((role) => {
        (breakdownByRoleType?.[role]?.[key] || []).forEach((entry) => {
          filtered.push(entry);
        });
      });
      if (filtered.length > 0) {
        return filtered;
      }
      return breakdown?.[key] || [];
    };

    const service = getEntries("service");
    const support = getEntries("support");
    const staffing = getEntries("staffing");
    const totals = intradaySelection.intradayLabor.totals || [];
    const roleData = intradaySelection.intradayLabor.by_role_type || {};
    const accumulator = {};

    const ensureEntry = (qhNum) => {
      const key = Number(qhNum);
      if (!accumulator[key]) {
        accumulator[key] = {
          service_hours: 0,
          support_hours: 0,
          staffing_hours: 0,
          actual_hours: 0,
        };
      }
      return accumulator[key];
    };

    service.forEach((entry) => {
      const record = ensureEntry(entry.qh_num);
      record.service_hours += Number(entry.hours || 0);
    });

    support.forEach((entry) => {
      const record = ensureEntry(entry.qh_num);
      record.support_hours += Number(entry.hours || 0);
    });

    staffing.forEach((entry) => {
      const record = ensureEntry(entry.qh_num);
      record.staffing_hours += Number(entry.hours || 0);
    });

    if (shouldFilterByDepartment) {
      selectedDepartments.forEach((role) => {
        (roleData?.[role] || []).forEach((entry) => {
          const record = ensureEntry(entry.qh_num);
          record.actual_hours += Number(entry.old_hours || 0);
        });
      });
    }

    if (Object.values(accumulator).every((entry) => entry.actual_hours === 0)) {
      totals.forEach((entry) => {
        const record = ensureEntry(entry.qh_num);
        record.actual_hours += Number(entry.old_hours || 0);
      });
    }

    return Object.keys(accumulator)
      .map((qhKey) => {
        const qhNum = Number(qhKey);
        const values = accumulator[qhKey];
        return {
          qh_num: qhNum,
          time: formatQuarterHourLabel(qhNum),
          service_hours: toIntradayLaborLevel(values.service_hours),
          support_hours: toIntradayLaborLevel(values.support_hours),
          staffing_hours: toIntradayLaborLevel(values.staffing_hours),
          actual_hours: toIntradayLaborLevel(values.actual_hours),
        };
      })
      .sort((a, b) => a.qh_num - b.qh_num);
  }, [
    intradaySelection,
    formatQuarterHourLabel,
    selectedDepartments,
    toIntradayLaborLevel,
  ]);

  const isForecastRun = runType === "forecasted_sandbox_vs_published";

  const activeIntradayChartData = React.useMemo(() => {
    if (!intradaySelection) {
      return [];
    }
    if (!isForecastRun) {
      return intradayActualBreakdownData;
    }
    return intradaySandboxAreaData.length
      ? intradaySandboxAreaData
      : intradayPublishedAreaData;
  }, [
    intradaySelection,
    isForecastRun,
    intradayActualBreakdownData,
    intradaySandboxAreaData,
    intradayPublishedAreaData,
  ]);

  const buildIntradayXAxisTicks = React.useCallback((chartData) => {
    if (!chartData || chartData.length === 0) {
      return [];
    }

    const desiredTickCount = 8;
    const step = Math.max(1, Math.floor(chartData.length / desiredTickCount));
    const ticks = [];

    for (let index = 0; index < chartData.length; index += step) {
      ticks.push(chartData[index].qh_num);
    }

    const finalTick = chartData[chartData.length - 1].qh_num;
    if (!ticks.includes(finalTick)) {
      ticks.push(finalTick);
    }

    return ticks;
  }, []);

  const intradayXAxisTicks = React.useMemo(
    () => buildIntradayXAxisTicks(activeIntradayChartData),
    [activeIntradayChartData, buildIntradayXAxisTicks]
  );

  const hasActiveIntradayChartData =
    isForecastRun
      ? intradaySandboxAreaData.length > 0 ||
        intradayPublishedAreaData.length > 0
      : activeIntradayChartData.length > 0;

  const formatQuarterHourBoundaryLabel = React.useCallback(
    (qhNum) => {
      const normalizedQh = ((Number(qhNum) % 24) + 24) % 24;
      return formatQuarterHourLabel(normalizedQh);
    },
    [formatQuarterHourLabel]
  );

  const forecastIntradayDeltaModel = React.useMemo(() => {
    if (!isForecastRun) {
      return { windows: [], byQh: {} };
    }

    const qhLookup = {};
    const ensureRecord = (row) => {
      const qhNum = Number(row.qh_num);
      if (!Number.isFinite(qhNum)) {
        return null;
      }
      if (!qhLookup[qhNum]) {
        qhLookup[qhNum] = {
          qhNum,
          publishedLabor: 0,
          publishedService: 0,
          publishedSupport: 0,
          publishedStaffing: 0,
          sandboxLabor: 0,
          sandboxService: 0,
          sandboxSupport: 0,
          sandboxStaffing: 0,
          scheduledHours: 0,
        };
      }
      return qhLookup[qhNum];
    };

    intradayPublishedAreaData.forEach((row) => {
      const record = ensureRecord(row);
      if (!record) {
        return;
      }
      const service = Number(row.service_hours) || 0;
      const support = Number(row.support_hours) || 0;
      const staffing = Number(row.staffing_hours) || 0;
      record.publishedService += service;
      record.publishedSupport += support;
      record.publishedStaffing += staffing;
      record.publishedLabor += service + support + staffing;
    });

    intradaySandboxAreaData.forEach((row) => {
      const record = ensureRecord(row);
      if (!record) {
        return;
      }
      const service = Number(row.service_hours) || 0;
      const support = Number(row.support_hours) || 0;
      const staffing = Number(row.staffing_hours) || 0;
      record.sandboxService += service;
      record.sandboxSupport += support;
      record.sandboxStaffing += staffing;
      record.sandboxLabor += service + support + staffing;
      record.scheduledHours += Number(row.scheduled_hours) || 0;
    });

    const rows = Object.values(qhLookup);
    const byQh = {};

    rows.forEach((row) => {
      byQh[row.qhNum] = {
        [INTRADAY_PUBLISHED_REFERENCE_KEY]:
          Math.round(row.publishedLabor * 100) / 100,
        [INTRADAY_SANDBOX_REFERENCE_KEY]:
          Math.round(row.sandboxLabor * 100) / 100,
        published_service_hours: Math.round(row.publishedService * 100) / 100,
        published_support_hours: Math.round(row.publishedSupport * 100) / 100,
        published_staffing_hours: Math.round(row.publishedStaffing * 100) / 100,
        sandbox_service_hours: Math.round(row.sandboxService * 100) / 100,
        sandbox_support_hours: Math.round(row.sandboxSupport * 100) / 100,
        sandbox_staffing_hours: Math.round(row.sandboxStaffing * 100) / 100,
        scheduled_hours: Math.round(row.scheduledHours * 100) / 100,
      };
    });

    return { byQh };
  }, [
    intradayPublishedAreaData,
    intradaySandboxAreaData,
    isForecastRun,
  ]);
  const forecastIntradayDeltaByQh = forecastIntradayDeltaModel.byQh;

  const forecastIntradayComparisonData = React.useMemo(
    () =>
      Object.keys(forecastIntradayDeltaByQh)
        .map((qhKey) => {
          const qhNum = Number(qhKey);
          const deltaValues = forecastIntradayDeltaByQh[qhNum] || {};
          const publishedLabor =
            deltaValues[INTRADAY_PUBLISHED_REFERENCE_KEY] || 0;
          const sandboxLabor =
            deltaValues[INTRADAY_SANDBOX_REFERENCE_KEY] || 0;
          const isShowingSandboxTarget =
            intradayForecastTargetMode === "sandbox";
          const serviceHours = isShowingSandboxTarget
            ? deltaValues.sandbox_service_hours || 0
            : deltaValues.published_service_hours || 0;
          const supportHours = isShowingSandboxTarget
            ? deltaValues.sandbox_support_hours || 0
            : deltaValues.published_support_hours || 0;
          const staffingHours = isShowingSandboxTarget
            ? deltaValues.sandbox_staffing_hours || 0
            : deltaValues.published_staffing_hours || 0;
          const selectedLabor = isShowingSandboxTarget
            ? sandboxLabor
            : publishedLabor;
          return {
            qh_num: qhNum,
            time: formatQuarterHourLabel(qhNum),
            service_hours: serviceHours,
            support_hours: supportHours,
            staffing_hours: staffingHours,
            [INTRADAY_SELECTED_TARGET_KEY]: selectedLabor,
            [INTRADAY_PUBLISHED_REFERENCE_KEY]: publishedLabor,
            [INTRADAY_SANDBOX_REFERENCE_KEY]: sandboxLabor,
            published_service_hours: deltaValues.published_service_hours || 0,
            published_support_hours: deltaValues.published_support_hours || 0,
            published_staffing_hours: deltaValues.published_staffing_hours || 0,
            sandbox_service_hours: deltaValues.sandbox_service_hours || 0,
            sandbox_support_hours: deltaValues.sandbox_support_hours || 0,
            sandbox_staffing_hours: deltaValues.sandbox_staffing_hours || 0,
            scheduled_hours: deltaValues.scheduled_hours || 0,
            sandbox_labor_total: sandboxLabor,
            published_labor_total: publishedLabor,
          };
        })
        .filter((row) => Number.isFinite(row.qh_num))
        .sort((a, b) => Number(a.qh_num) - Number(b.qh_num)),
    [
      forecastIntradayDeltaByQh,
      formatQuarterHourLabel,
      intradayForecastTargetMode,
    ]
  );

  const forecastIntradayXAxisTicks = React.useMemo(
    () => buildIntradayXAxisTicks(forecastIntradayComparisonData),
    [buildIntradayXAxisTicks, forecastIntradayComparisonData]
  );

  const numberOfLocationsInTest = data ? Object.keys(data).length : 0;

  // Initialize an array to store the sums
  let locationSumsByDayOfWeekArray = [];

  // Iterate over each index position
  if (!isVolumeSensitivity) {
    for (let i = 0; i < 7; i++) {
      // Initialize sum for current index position
      let sales = 0;
      let new_hours = 0;
      let new_cost = 0;
      let old_hours = 0;
      let old_cost = 0;

      if (data) {
        Object.values(data).forEach((day) => {
          const laborResultsByDepartment = calculateSum(
            day[i].labor_results,
            selectedFields,
            selectedDepartments
          );
          sales += parseFloat(day[i].sales);
          new_hours += parseFloat(laborResultsByDepartment.new_hours);
          new_cost += parseFloat(laborResultsByDepartment.new_cost);
          old_hours += parseFloat(laborResultsByDepartment.old_hours);
          old_cost += parseFloat(laborResultsByDepartment.old_cost);
        });
      }

      // Push the sum to the sumsArray
      locationSumsByDayOfWeekArray.push({
        sales: sales,
        new_hours: new_hours,
        new_cost: new_cost,
        old_hours: old_hours,
        old_cost: old_cost,
      });
    }
  }

  function sumWeekData(weekData) {
    // Initialize variables to store cumulative sums
    let totalNewCost = 0;
    let totalNewHours = 0;
    let totalOldCost = 0;
    let totalOldHours = 0;
    let totalSales = 0;

    // Iterate through each day's data in the array
    weekData.forEach((day) => {
      // Add each day's values to the cumulative sums
      totalNewCost += day.new_cost;
      totalNewHours += day.new_hours;
      totalOldCost += day.old_cost;
      totalOldHours += day.old_hours;
      totalSales += day.sales;
    });

    // Create and return a new object with summed values
    return {
      new_cost: totalNewCost,
      new_hours: totalNewHours,
      old_cost: totalOldCost,
      old_hours: totalOldHours,
      sales: totalSales,
    };
  }

  const weekdayLabels = [
    "Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday",
  ];
  const xAxisLabels = Array.from(
    { length: 7 },
    (_, index) => weekdayLabels[(businessWeekStartDayIndex + index) % 7]
  );

  const averageHoursArray = [];
  const averageSPLHArray = [];
  const averageLaborPercentArray = [];

  if (!isVolumeSensitivity && locationSumsByDayOfWeekArray.length > 0) {
    xAxisLabels.forEach((day, index) => {
      const averageHoursObj = {
        name: day,
        new_hours: Number(
          (
            locationSumsByDayOfWeekArray[index].new_hours /
            numberOfLocationsInTest
          ).toFixed(2)
        ),
        old_hours: Number(
          (
            locationSumsByDayOfWeekArray[index].old_hours /
            numberOfLocationsInTest
          ).toFixed(2)
        ),
      };
      averageHoursArray.push(averageHoursObj);

      const averageSPLHObj = {
        name: day,
        new_SPLH: Number(
          (
            locationSumsByDayOfWeekArray[index].sales /
            locationSumsByDayOfWeekArray[index].new_hours
          ).toFixed(2)
        ),
        old_SPLH: Number(
          (
            locationSumsByDayOfWeekArray[index].sales /
            locationSumsByDayOfWeekArray[index].old_hours
          ).toFixed(2)
        ),
      };
      averageSPLHArray.push(averageSPLHObj);

      const averageLaborPercentObj = {
        name: day,
        new_labor_percent: Number(
          (
            (locationSumsByDayOfWeekArray[index].new_cost /
              locationSumsByDayOfWeekArray[index].sales) *
            100
          ).toFixed(2)
        ),
        old_labor_percent: Number(
          (
            (locationSumsByDayOfWeekArray[index].old_cost /
              locationSumsByDayOfWeekArray[index].sales) *
            100
          ).toFixed(2)
        ),
      };
      averageLaborPercentArray.push(averageLaborPercentObj);
    });
  }

  // console.log("AVERAGE LABOR PERCENT ARRAY", locationSumsByDayOfWeekArray);
  // console.log("AVERAGE HOURS ARRAY", averageHoursArray);
  // console.log("STORE NAMES", props.storeNames);

  const volumeSensitivityData = React.useMemo(() => {
    if (!isVolumeSensitivity || !data) {
      return null;
    }

    const storeTables = Object.entries(data).map(([storeName, entries]) => {
      const volumesSet = new Set();
      const dayLookup = {};

      entries.forEach((entry) => {
        const dayName = entry.day_name;
        const volume = Number(entry.volume);
        volumesSet.add(volume);
        if (!dayLookup[dayName]) {
          dayLookup[dayName] = {};
        }
        dayLookup[dayName][volume] = entry;
      });

      const volumes = Array.from(volumesSet).sort((a, b) => a - b);
      return { storeName, volumes, dayLookup };
    });

    const scatterSeries = {};
    xAxisLabels.forEach((day) => {
      scatterSeries[day] = [];
    });

    Object.entries(data).forEach(([storeName, entries]) => {
      entries.forEach((entry) => {
        const totals = calculateNewTotals(
          entry.labor_results,
          selectedDepartments
        );
        const sales = Number(entry.sales) || 0;
        const newHours = Number(totals.new_hours) || 0;
        const newCost = Number(totals.new_cost) || 0;
        const value =
          selectedMetric === "hours"
            ? newHours
            : selectedMetric === "splh"
            ? newHours > 0
              ? sales / newHours
              : 0
            : selectedMetric === "labor_percent"
            ? sales > 0
              ? (newCost / sales) * 100
              : 0
            : 0;
        const dayName = entry.day_name;
        if (!scatterSeries[dayName]) {
          scatterSeries[dayName] = [];
        }
        scatterSeries[dayName].push({
          sales,
          value,
          dayName,
          storeName,
          volume: Number(entry.volume) || 0,
        });
      });
    });

    return { storeTables, scatterSeries };
  }, [
    data,
    isVolumeSensitivity,
    selectedDepartments,
    selectedMetric,
    xAxisLabels,
  ]);

  // Finds the store name if one store is selected
  let selectedStoreId = selectedStores[0];
  let store = props.storeNames.find((store) => store.id === selectedStoreId);
  let storeName = store ? store.store_name : null;

  // Labor standards contribution data
  const transformedLaborStandardsContributionData =
    laborStandardContributionData
      ? Object.keys(laborStandardContributionData)
          .map((key) => ({
            name: key,
            hours: parseFloat(laborStandardContributionData[key].hours).toFixed(
              2
            ),
            role_type: laborStandardContributionData[key].role_type,
            type: laborStandardContributionData[key].type,
          }))
          .sort((a, b) => b.hours - a.hours)
      : [];

  const laborStandardsDataByDepartment =
    transformedLaborStandardsContributionData?.filter((item) =>
      selectedDepartments?.includes(item.role_type)
    );

  const laborStandardsMaxHours = Math.round(
    Math.max(...laborStandardsDataByDepartment.map((d) => d.hours)) * 1.05
  );

  // Recharts custom components
  const CustomLegend = (props) => {
    const { payload } = props;
    const visiblePayload = (payload || []).filter(
      (entry) =>
        !INTRADAY_DELTA_AREA_KEYS.has(entry.value) &&
        !INTRADAY_DELTA_AREA_KEYS.has(entry.dataKey)
    );
    return (
      <div className="w-full flex items-center justify-center space-x-5 mt-3">
        {visiblePayload.map((entry, index) => (
          <div
            key={index}
            style={{ color: entry.color }}
            className="flex items-center space-x-1"
          >
            <div
              style={{ backgroundColor: entry.color }}
              className="w-2 h-2 rounded-full"
            ></div>
            <p>
              {(() => {
                const labelMap = {
                  old_hours:
                    runType === "forecasted_sandbox_vs_published"
                      ? "Published Hours"
                      : "Actual Hours",
                  new_hours: `${workingModelLabel} Hours`,
                  hours: `${workingModelLabel} Hours`,
                  old_SPLH:
                    runType === "forecasted_sandbox_vs_published"
                      ? "Published SPLH"
                      : "Actual SPLH",
                  new_SPLH: `${workingModelLabel} SPLH`,
                  SPLH: `${workingModelLabel} SPLH`,
                  old_labor_percent:
                    runType === "forecasted_sandbox_vs_published"
                      ? "Published Labor Percent"
                      : "Actual Labor Percent",
                  new_labor_percent: `${workingModelLabel} Labor Percent`,
                  labor_percent: `${workingModelLabel} Labor Percent`,
                  service_hours: "Service Labor",
                  support_hours: "Support Labor",
                  staffing_hours: "Staffing Labor",
                  [INTRADAY_PUBLISHED_REFERENCE_KEY]: "Published Labor",
                  [INTRADAY_SANDBOX_REFERENCE_KEY]: `${workingModelLabel} Labor`,
                  scheduled_hours: "Scheduled Labor",
                  actual_hours:
                    runType === "forecasted_sandbox_vs_published"
                      ? "Published Hours"
                      : "Actual Labor",
                };
                return (
                  labelMap[entry.value] ||
                  labelMap[entry.dataKey] ||
                  entry.value ||
                  entry.dataKey ||
                  ""
                );
              })()}
            </p>
          </div>
        ))}
      </div>
    );
  };

  const formatTooltipNumber = (value, decimals = 2) => {
    const numericValue = Number(value) || 0;
    const roundedValue =
      Math.round(numericValue * Math.pow(10, decimals)) /
      Math.pow(10, decimals);
    return Number.isInteger(roundedValue)
      ? `${roundedValue}`
      : roundedValue.toFixed(decimals);
  };

  const CustomTooltip = ({ payload, label }) => {
    const visiblePayload = (payload || []).filter(
      (item) => !INTRADAY_DELTA_AREA_KEYS.has(item.dataKey)
    );
    const row = visiblePayload[0]?.payload || {};
    const qhNum = Number(row.qh_num ?? label);
    const timeLabel = Number.isFinite(qhNum)
      ? `${formatQuarterHourBoundaryLabel(
          qhNum
        )} - ${formatQuarterHourBoundaryLabel(qhNum + 0.25)}`
      : label;
    const isForecastTooltip =
      Object.prototype.hasOwnProperty.call(row, "sandbox_labor_total") &&
      Object.prototype.hasOwnProperty.call(row, "published_labor_total");
    const isActualLaborTooltip =
      Object.prototype.hasOwnProperty.call(row, "actual_hours") &&
      Object.prototype.hasOwnProperty.call(row, "service_hours") &&
      Object.prototype.hasOwnProperty.call(row, "support_hours");

    if (isForecastTooltip) {
      const publishedLabor = Number(row.published_labor_total) || 0;
      const sandboxLabor = Number(row.sandbox_labor_total) || 0;
      const deltaLabor = sandboxLabor - publishedLabor;
      const deltaHours = deltaLabor / 4;
      const isSandboxOverPublished = deltaLabor > 0.004;
      const isSandboxUnderPublished = deltaLabor < -0.004;
      const deltaDirection =
        isSandboxOverPublished
          ? "over published"
          : isSandboxUnderPublished
          ? "under published"
          : "matching published";
      const deltaColor =
        isSandboxOverPublished
          ? "#be123c"
          : isSandboxUnderPublished
          ? "#2563eb"
          : "#475569";
      const deltaSign = isSandboxOverPublished
        ? "+"
        : isSandboxUnderPublished
        ? "-"
        : "";
      const currentLabel =
        intradayForecastTargetMode === "sandbox"
          ? workingModelLabel
          : "Published";
      const currentService = Number(row.service_hours) || 0;
      const currentSupport = Number(row.support_hours) || 0;
      const currentStaffing = Number(row.staffing_hours) || 0;

      return (
        <div className="min-w-[260px] rounded-lg border border-slate-300 bg-white p-4 text-xs text-slate-600 shadow-lg">
          <div className="text-sm font-semibold text-slate-900">
            {timeLabel}
          </div>
          <div className="mt-1 text-slate-500">
            Showing {currentLabel} target labor
          </div>
          <div className="mt-3 space-y-1.5">
            <div className="flex items-center justify-between gap-6">
              <span className="flex items-center gap-2">
                <span
                  className="h-2.5 w-2.5 rounded-full"
                  style={{ backgroundColor: INTRADAY_SERVICE_COLOR }}
                />
                Service labor level
              </span>
              <span className="font-medium text-slate-900">
                {formatTooltipNumber(currentService)}
              </span>
            </div>
            <div className="flex items-center justify-between gap-6">
              <span className="flex items-center gap-2">
                <span
                  className="h-2.5 w-2.5 rounded-full"
                  style={{ backgroundColor: INTRADAY_SUPPORT_COLOR }}
                />
                Support labor level
              </span>
              <span className="font-medium text-slate-900">
                {formatTooltipNumber(currentSupport)}
              </span>
            </div>
            {currentStaffing > 0.004 && (
              <div className="flex items-center justify-between gap-6">
                <span className="flex items-center gap-2">
                  <span
                    className="h-2.5 w-2.5 rounded-full"
                    style={{ backgroundColor: INTRADAY_STAFFING_COLOR }}
                  />
                  Staffing labor level
                </span>
                <span className="font-medium text-slate-900">
                  {formatTooltipNumber(currentStaffing)}
                </span>
              </div>
            )}
          </div>
          <div className="mt-3 border-t border-slate-200 pt-3 space-y-1.5">
            <div className="flex items-center justify-between gap-6">
              <span>Published target</span>
              <span className="font-medium text-slate-900">
                {formatTooltipNumber(publishedLabor)}
              </span>
            </div>
            <div className="flex items-center justify-between gap-6">
              <span>{workingModelLabel} target</span>
              <span className="font-medium text-slate-900">
                {formatTooltipNumber(sandboxLabor)}
              </span>
            </div>
            <div
              className="flex items-center justify-between gap-6 font-medium"
              style={{ color: deltaColor }}
            >
              <span>{workingModelLabel} is {deltaDirection}</span>
              <span>
                {deltaSign}
                {formatTooltipNumber(Math.abs(deltaLabor))} labor
              </span>
            </div>
            <div className="text-slate-500">
              {deltaSign}
              {formatTooltipNumber(Math.abs(deltaHours))} labor hours in this
              15-minute interval.
            </div>
            {showIntradayScheduledHours && (
              <div className="flex items-center justify-between gap-6 pt-1">
                <span>Scheduled labor level</span>
                <span className="font-medium text-slate-900">
                  {formatTooltipNumber(row.scheduled_hours)}
                </span>
              </div>
            )}
          </div>
        </div>
      );
    }

    if (isActualLaborTooltip) {
      const serviceLabor = Number(row.service_hours) || 0;
      const supportLabor = Number(row.support_hours) || 0;
      const staffingLabor = Number(row.staffing_hours) || 0;
      const earnedLabor = serviceLabor + supportLabor + staffingLabor;
      const actualLabor = Number(row.actual_hours) || 0;

      return (
        <div className="min-w-[260px] rounded-lg border border-slate-300 bg-white p-4 text-xs text-slate-600 shadow-lg">
          <div className="text-sm font-semibold text-slate-900">
            {timeLabel}
          </div>
          <div className="mt-1 text-slate-500">
            Showing earned target and actual labor
          </div>
          <div className="mt-3 space-y-1.5">
            <div className="flex items-center justify-between gap-6">
              <span className="flex items-center gap-2">
                <span
                  className="h-2.5 w-2.5 rounded-full"
                  style={{ backgroundColor: INTRADAY_SERVICE_COLOR }}
                />
                Service labor level
              </span>
              <span className="font-medium text-slate-900">
                {formatTooltipNumber(serviceLabor)}
              </span>
            </div>
            <div className="flex items-center justify-between gap-6">
              <span className="flex items-center gap-2">
                <span
                  className="h-2.5 w-2.5 rounded-full"
                  style={{ backgroundColor: INTRADAY_SUPPORT_COLOR }}
                />
                Support labor level
              </span>
              <span className="font-medium text-slate-900">
                {formatTooltipNumber(supportLabor)}
              </span>
            </div>
            {staffingLabor > 0.004 && (
              <div className="flex items-center justify-between gap-6">
                <span className="flex items-center gap-2">
                  <span
                    className="h-2.5 w-2.5 rounded-full"
                    style={{ backgroundColor: INTRADAY_STAFFING_COLOR }}
                  />
                  Staffing labor level
                </span>
                <span className="font-medium text-slate-900">
                  {formatTooltipNumber(staffingLabor)}
                </span>
              </div>
            )}
          </div>
          <div className="mt-3 border-t border-slate-200 pt-3 space-y-1.5">
            <div className="flex items-center justify-between gap-6">
              <span>Earned target</span>
              <span className="font-medium text-slate-900">
                {formatTooltipNumber(earnedLabor)}
              </span>
            </div>
            <div className="flex items-center justify-between gap-6">
              <span>Actual labor</span>
              <span className="font-medium text-slate-900">
                {formatTooltipNumber(actualLabor)}
              </span>
            </div>
            <div className="text-slate-500">
              Values are labor levels for this 15-minute interval.
            </div>
          </div>
        </div>
      );
    }

    if (visiblePayload.length) {
      return (
        <div className="rounded-lg border border-slate-300 bg-white p-4 text-xs text-slate-600 shadow-lg">
          <div className="text-sm font-semibold text-slate-900">
            {timeLabel}
          </div>
          {visiblePayload.map((item, index) => (
            <p key={index} style={{ color: item.color }} className="mt-2">
              {(() => {
                const tooltipMap = {
                  old_hours:
                    runType === "forecasted_sandbox_vs_published"
                      ? "Published Hours: "
                      : "Actual Hours: ",
                  new_hours: `${workingModelLabel} Hours: `,
                  hours: `${workingModelLabel} Hours: `,
                  old_SPLH:
                    runType === "forecasted_sandbox_vs_published"
                      ? "Published SPLH: "
                      : "Actual SPLH: ",
                  new_SPLH: `${workingModelLabel} SPLH: `,
                  SPLH: `${workingModelLabel} SPLH: `,
                  old_labor_percent:
                    runType === "forecasted_sandbox_vs_published"
                      ? "Published Labor Percent: "
                      : "Actual Labor Percent: ",
                  new_labor_percent: `${workingModelLabel} Labor Percent: `,
                  labor_percent: `${workingModelLabel} Labor Percent: `,
                  service_hours: "Service Labor: ",
                  support_hours: "Support Labor: ",
                  staffing_hours: "Staffing Labor: ",
                  [INTRADAY_PUBLISHED_REFERENCE_KEY]: "Published Labor: ",
                  [INTRADAY_SANDBOX_REFERENCE_KEY]: `${workingModelLabel} Labor: `,
                  scheduled_hours: "Scheduled Labor: ",
                  actual_hours:
                    runType === "forecasted_sandbox_vs_published"
                      ? "Published Hours: "
                      : "Actual Labor: ",
                };
                return tooltipMap[item.dataKey] || `${item.name || item.dataKey}: `;
              })()}
              {(item.dataKey === "old_SPLH" ||
                item.dataKey === "new_SPLH" ||
                item.dataKey === "SPLH") &&
                "$"}
              {item.value}
              {(item.dataKey === "old_labor_percent" ||
                item.dataKey === "new_labor_percent" ||
                item.dataKey === "labor_percent") &&
                "%"}
            </p>
          ))}
        </div>
      );
    }

    return null;
  };

  const formatValue = (value, includeSymbol = "", decimals = 2) => {
    if (value === 0 || isNaN(value) || value === undefined || value === null) {
      return "-";
    }
    return `${includeSymbol === "$" ? "$" : ""}${Number(value).toFixed(
      decimals
    )}${includeSymbol === "%" ? "%" : ""}`;
  };

  const getLaborDeltaBadgeClass = React.useCallback(
    (change, metric) => {
      const numericChange = Number(change);
      if (!Number.isFinite(numericChange) || Math.abs(numericChange) < 0.005) {
        return "bg-slate-100 border border-slate-200 text-slate-600";
      }

      const isActualVsEarned = runType === "earned_sandbox_vs_actual";
      const isBadDelta =
        metric === "splh"
          ? isActualVsEarned
            ? numericChange > 0
            : numericChange < 0
          : isActualVsEarned
          ? numericChange < 0
          : numericChange > 0;

      return isBadDelta
        ? "bg-rose-100 border border-rose-200 text-rose-800"
        : "bg-emerald-100 border border-emerald-200 text-emerald-800";
    },
    [runType]
  );

  const renderDeltaAreaBands = (showDeltaAreas) =>
    showDeltaAreas ? (
      <>
        <Area
          type="stepAfter"
          dataKey={INTRADAY_DELTA_INCREASE_BASE_KEY}
          stackId="sandboxIncreaseDelta"
          fill="transparent"
          stroke="transparent"
          isAnimationActive={false}
          activeDot={false}
        />
        <Area
          type="stepAfter"
          dataKey={INTRADAY_DELTA_INCREASE_GAP_KEY}
          stackId="sandboxIncreaseDelta"
          fill={INTRADAY_DELTA_INCREASE_COLOR}
          fillOpacity={0.4}
          stroke="transparent"
          isAnimationActive={false}
          activeDot={false}
        />
        <Area
          type="stepAfter"
          dataKey={INTRADAY_DELTA_DECREASE_BASE_KEY}
          stackId="sandboxDecreaseDelta"
          fill="transparent"
          stroke="transparent"
          isAnimationActive={false}
          activeDot={false}
        />
        <Area
          type="stepAfter"
          dataKey={INTRADAY_DELTA_DECREASE_GAP_KEY}
          stackId="sandboxDecreaseDelta"
          fill={INTRADAY_DELTA_DECREASE_COLOR}
          fillOpacity={0.4}
          stroke="transparent"
          isAnimationActive={false}
          activeDot={false}
        />
      </>
    ) : null;

  const renderComponentLegend = (
    overlayLabel,
    showPublishedReference = false
  ) => (
    <div className="mt-2 flex flex-wrap items-center gap-4 text-xs text-slate-600">
      <div className="flex items-center gap-2">
        <span
          className="h-2.5 w-2.5 rounded-full"
          style={{ backgroundColor: INTRADAY_SERVICE_COLOR }}
        />
        Service
      </div>
      <div className="flex items-center gap-2">
        <span
          className="h-2.5 w-2.5 rounded-full"
          style={{ backgroundColor: INTRADAY_SUPPORT_COLOR }}
        />
        Support
      </div>
      <div className="flex items-center gap-2">
        <span
          className="h-2.5 w-2.5 rounded-full"
          style={{ backgroundColor: INTRADAY_STAFFING_COLOR }}
        />
        Staffing
      </div>
      <div className="flex items-center gap-2">
        <span
          className="h-2.5 w-2.5 rounded-full"
          style={{ backgroundColor: INTRADAY_PRIMARY_COLOR }}
        />
        {overlayLabel}
      </div>
      {showPublishedReference && (
        <div className="flex items-center gap-2">
          <span
            className="h-0 w-4 border-t border-dashed"
            style={{ borderColor: INTRADAY_PUBLISHED_REFERENCE_COLOR }}
          />
          Published Labor
        </div>
      )}
    </div>
  );

  const renderForecastTargetLegend = (showScheduled) => (
    <div className="mt-2 flex flex-wrap items-center gap-4 text-xs text-slate-600">
      <div className="flex items-center gap-2">
        <span
          className="h-2.5 w-2.5 rounded-full"
          style={{ backgroundColor: INTRADAY_SERVICE_COLOR }}
        />
        Service
      </div>
      <div className="flex items-center gap-2">
        <span
          className="h-2.5 w-2.5 rounded-full"
          style={{ backgroundColor: INTRADAY_SUPPORT_COLOR }}
        />
        Support
      </div>
      <div className="flex items-center gap-2">
        <span
          className="h-2.5 w-2.5 rounded-full"
          style={{ backgroundColor: INTRADAY_STAFFING_COLOR }}
        />
        Staffing
      </div>
      {showScheduled && (
        <div className="flex items-center gap-2">
          <span
            className="h-2.5 w-2.5 rounded-full"
            style={{ backgroundColor: INTRADAY_PRIMARY_COLOR }}
          />
          Scheduled Labor
        </div>
      )}
    </div>
  );

  const renderForecastIntradayChart = (
    chartData,
    xAxisTicks,
    showScheduled
  ) => (
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart
        data={chartData}
        margin={{ top: 10, right: 24, left: 12, bottom: 10 }}
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis
          dataKey="qh_num"
          type="number"
          domain={["dataMin", (dataMax) => dataMax + 1]}
          ticks={xAxisTicks}
          minTickGap={16}
          tick={{ fontSize: 12 }}
          tickFormatter={formatQuarterHourBoundaryLabel}
        />
        <YAxis
          label={{
            value: "Labor",
            angle: -90,
            position: "insideLeft",
            style: { textAnchor: "middle", fill: "#64748b", fontSize: 12 },
          }}
        />
        <Tooltip content={<CustomTooltip />} />
        <Area
          type="stepAfter"
          dataKey="service_hours"
          name="Service"
          stackId="forecastTarget"
          stroke={INTRADAY_SERVICE_COLOR}
          fill={INTRADAY_SERVICE_COLOR}
          fillOpacity={0.58}
          isAnimationActive={true}
          animationBegin={0}
          animationDuration={500}
          animationEasing="ease-out"
          activeDot={false}
        />
        <Area
          type="stepAfter"
          dataKey="support_hours"
          name="Support"
          stackId="forecastTarget"
          stroke={INTRADAY_SUPPORT_COLOR}
          fill={INTRADAY_SUPPORT_COLOR}
          fillOpacity={0.58}
          isAnimationActive={true}
          animationBegin={0}
          animationDuration={500}
          animationEasing="ease-out"
          activeDot={false}
        />
        <Area
          type="stepAfter"
          dataKey="staffing_hours"
          name="Staffing"
          stackId="forecastTarget"
          stroke={INTRADAY_STAFFING_COLOR}
          fill={INTRADAY_STAFFING_COLOR}
          fillOpacity={0.58}
          isAnimationActive={true}
          animationBegin={0}
          animationDuration={500}
          animationEasing="ease-out"
          activeDot={false}
        />
        <Line
          type="stepAfter"
          dataKey={INTRADAY_SELECTED_TARGET_KEY}
          name="Selected target"
          stroke={INTRADAY_PUBLISHED_REFERENCE_COLOR}
          strokeWidth={1.4}
          dot={false}
          isAnimationActive={false}
        />
        {showScheduled && (
          <Line
            type="stepAfter"
            dataKey="scheduled_hours"
            name="Scheduled Labor"
            stroke={INTRADAY_PRIMARY_COLOR}
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
          />
        )}
      </ComposedChart>
    </ResponsiveContainer>
  );

  const renderComponentIntradayChart = (
    chartData,
    xAxisTicks,
    overlayDataKey,
    overlayLabel,
    deltaWindows = [],
    showDeltaAreas = true,
    showPublishedReference = false
  ) => (
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart
        data={chartData}
        margin={{ top: 10, right: 24, left: 12, bottom: 10 }}
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis
          dataKey="qh_num"
          type="number"
          domain={["dataMin", (dataMax) => dataMax + 1]}
          ticks={xAxisTicks}
          minTickGap={16}
          tick={{ fontSize: 12 }}
          tickFormatter={formatQuarterHourBoundaryLabel}
        />
        <YAxis
          label={{
            value: "Labor",
            angle: -90,
            position: "insideLeft",
            style: { textAnchor: "middle", fill: "#64748b", fontSize: 12 },
          }}
        />
        <Tooltip content={<CustomTooltip />} />
        <Area
          type="stepAfter"
          dataKey="service_hours"
          stackId="1"
          stroke={INTRADAY_SERVICE_COLOR}
          fill={INTRADAY_SERVICE_COLOR}
          isAnimationActive={false}
        />
        <Area
          type="stepAfter"
          dataKey="support_hours"
          stackId="1"
          stroke={INTRADAY_SUPPORT_COLOR}
          fill={INTRADAY_SUPPORT_COLOR}
          isAnimationActive={false}
        />
        <Area
          type="stepAfter"
          dataKey="staffing_hours"
          stackId="1"
          stroke={INTRADAY_STAFFING_COLOR}
          fill={INTRADAY_STAFFING_COLOR}
          isAnimationActive={false}
        />
        {renderDeltaAreaBands(showDeltaAreas && deltaWindows?.length > 0)}
        {showPublishedReference && (
          <Line
            type="stepAfter"
            dataKey={INTRADAY_PUBLISHED_REFERENCE_KEY}
            name="Published Labor"
            stroke={INTRADAY_PUBLISHED_REFERENCE_COLOR}
            strokeDasharray="5 4"
            strokeWidth={1.8}
            dot={false}
            isAnimationActive={false}
          />
        )}
        <Line
          type="stepAfter"
          dataKey={overlayDataKey}
          name={overlayLabel}
          stroke={INTRADAY_PRIMARY_COLOR}
          strokeWidth={2}
          dot={false}
          isAnimationActive={false}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );

  return (
    <div>
      <Modal
        open={props.open}
        onClose={props.handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <button
            type="button"
            className="absolute right-4 top-4 z-10 rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-600"
            onClick={props.handleClose}
            aria-label="Close detailed model evaluation"
          >
            <CancelIcon />
          </button>
          <div className="border-b border-slate-200 bg-white px-6 py-5 pr-16">
            <h1 id="modal-modal-title" className="text-xl font-semibold text-slate-900">
              Evaluate {workingModelLabel}
            </h1>
            <p id="modal-modal-description" className="mt-1 text-sm text-slate-600">
              Customize the stores, dates, and analysis when you need more than
              the quick impact summary.
            </p>
            <div className="mt-5 flex items-center gap-5 lg:flex-wrap sm:flex-col sm:items-stretch sm:gap-3">
              <div className="flex w-[340px] shrink-0 items-center sm:w-full">
                <div className="min-w-0 flex-1">
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DesktopDatePicker
                      value={startDate}
                      onChange={(newValue) => setStartDate(newValue)}
                      slotProps={{
                        day: {
                          sx: {
                            "&.MuiPickersDay-root.Mui-selected": {
                              backgroundColor: "#6d28d9",
                            },
                          },
                        },
                        textField: {
                          inputProps: { "aria-label": "Start date" },
                        },
                      }}
                      sx={evaluationDateStyles}
                      className="w-full"
                    />
                  </LocalizationProvider>
                </div>
                <ArrowForwardIcon
                  className="mx-2 shrink-0 text-slate-500"
                  sx={{ fontSize: "18px" }}
                  aria-hidden="true"
                />
                <div className="min-w-0 flex-1">
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DesktopDatePicker
                      value={endDate}
                      onChange={(newValue) => setEndDate(newValue)}
                      slotProps={{
                        day: {
                          sx: {
                            "&.MuiPickersDay-root.Mui-selected": {
                              backgroundColor: "#6d28d9",
                            },
                          },
                        },
                        textField: {
                          inputProps: { "aria-label": "End date" },
                        },
                      }}
                      sx={evaluationDateStyles}
                      className="w-full"
                    />
                  </LocalizationProvider>
                </div>
              </div>
              <div className="w-[420px] min-w-0 shrink lg:flex-1 sm:w-full">
                <Select
                  fullWidth
                  multiple
                  value={selectedStores}
                  onChange={handleStoreSelectionChange}
                  input={<OutlinedInput />}
                  inputProps={{ "aria-label": "Locations to evaluate" }}
                  sx={{ ...evaluationSelectStyles, paddingX: "15px" }}
                  renderValue={(selected) => {
                    const selectedStoresNames = props.storeNames
                      .filter((store) => selected.includes(store.id))
                      .map((store) => store.store_name);

                    if (selectedStoresNames.length === props.storeNames.length) {
                      return `All ${selectedStoresNames.length} locations`;
                    }
                    if (selectedStoresNames.length <= 3) {
                      return selectedStoresNames.join(", ");
                    }
                    return `${selectedStoresNames.length} locations selected`;
                  }}
                  className="text-left w-full"
                >
                  <MenuItem value="all">
                    <Checkbox
                      checked={
                        props.storeNames.length > 0 &&
                        selectedStores.length === props.storeNames.length
                      }
                      indeterminate={
                        selectedStores.length > 0 &&
                        selectedStores.length < props.storeNames.length
                      }
                      sx={{
                        color: "#6d28d9",
                        "&.Mui-checked": {
                          color: "#6d28d9", // Purple color when checked
                        },
                        "&.MuiCheckbox-indeterminate": {
                          color: "#6d28d9", // Purple color when indeterminate
                        },
                      }}
                    />
                    <ListItemText
                      primary={
                        selectedStores.length === props.storeNames.length
                          ? "Deselect All"
                          : "Select All"
                      }
                      primaryTypographyProps={{ style: { fontWeight: "bold" } }}
                    />
                  </MenuItem>
                  {props.storeNames.map((value) => (
                    <MenuItem key={value.id} value={value.id}>
                      <Checkbox
                        checked={selectedStores.indexOf(value.id) > -1}
                        sx={{
                          color: "#6d28d9",
                          "&.Mui-checked": {
                            color: "#6d28d9",
                          },
                        }}
                      />
                      <ListItemText primary={value.store_name} />
                    </MenuItem>
                  ))}
                </Select>
              </div>
              <div className="min-w-[260px] flex-1 sm:min-w-0 sm:w-full">
                <Select
                  value={runType}
                  onChange={handleRunTypeChange}
                  fullWidth
                  size="small"
                  inputProps={{ "aria-label": "Analysis type" }}
                  sx={evaluationSelectStyles}
                >
                  <MenuItem value="forecasted_sandbox_vs_published">
                    Forecast impact vs published
                  </MenuItem>
                  <MenuItem value="earned_sandbox_vs_actual">
                    Earned labor vs actual labor
                  </MenuItem>
                  <MenuItem value="volume_sensitivity_analysis">
                    Advanced: volume sensitivity
                  </MenuItem>
                </Select>
              </div>
              <div className="sm:w-full">
                <LoadingButton
                  className="sm:w-full"
                  variant="contained"
                  loading={calculationStatus === "loading"}
                  loadingPosition="end"
                  endIcon={
                    calculationStatus === "completed" ? (
                      <CheckIcon />
                    ) : (
                      <AutoGraphIcon />
                    )
                  }
                  onClick={() => handleCalculateClick()}
                  disabled={
                    selectedStores.length === 0 ||
                    calculationStatus === "loading" ||
                    (isVolumeSensitivity && !isVolumeInputValid)
                  }
                  sx={{
                    textTransform: "none",
                    borderRadius: "12px",
                    height: "40px",
                    backgroundColor:
                      calculationStatus === "completed" ? "green" : "#6d28d9", // conditional styling
                    "&:hover": {
                      backgroundColor:
                        calculationStatus === "completed"
                          ? "darkgreen"
                          : "#5b21b6", // change color on hover if completed
                    },
                  }}
                >
                  {calculationStatus === "loading"
                    ? "Evaluating..."
                    : calculationStatus === "completed"
                    ? "Evaluation complete"
                    : "Run evaluation"}
                </LoadingButton>
              </div>
              {calculationStatus === "completed" && (
                <button
                  type="button"
                  className="justify-self-start rounded-lg px-2 py-2 text-sm font-semibold text-violet-700 hover:bg-violet-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-600"
                  onClick={() => setCalculationStatus("idle")}
                >
                  Reset
                </button>
              )}
            </div>
            {isVolumeSensitivity && (
              <div className="w-full flex flex-wrap gap-4 mt-4">
                <TextField
                  label="Min Volume"
                  value={volumeMin}
                  onChange={handleVolumeMinChange}
                  size="small"
                  type="number"
                  sx={volumeFieldStyles}
                />
                <TextField
                  label="Max Volume"
                  value={volumeMax}
                  onChange={handleVolumeMaxChange}
                  size="small"
                  type="number"
                  sx={volumeFieldStyles}
                />
                <TextField
                  label="Volume Increment"
                  value={volumeIncrement}
                  onChange={handleVolumeIncrementChange}
                  size="small"
                  type="number"
                  sx={{ ...volumeFieldStyles, minWidth: "160px" }}
                />
              </div>
            )}
          </div>
          {calculationStatus === "idle" || calculationStatus === "loading" ? (
            <div className="flex min-h-[70%] w-full flex-grow items-center justify-center bg-slate-50 px-6">
              <div className="max-w-lg pb-12 text-center">
                <AutoGraphIcon className="text-slate-400" sx={{ fontSize: 34 }} />
                <h3 className="mt-3 text-base font-semibold text-slate-800">
                  {calculationStatus === "loading"
                    ? "Evaluating model impact…"
                    : "Set up a detailed evaluation"}
                </h3>
                <p className="mt-1 text-sm text-slate-500">
                  {calculationStatus === "loading"
                    ? "This can take a moment for larger date ranges."
                    : "Choose the scope above, then run the evaluation to compare labor results."}
                </p>
              </div>
            </div>
          ) : (
            <div className="w-full min-h-[82%] bg-slate-50 px-6 pb-12">
              <div className="flex w-full flex-wrap justify-between gap-4 pt-8">
                <div className="flex flex-wrap items-center gap-4">
                  <div className="flex border border-slate-200 rounded-xl overflow-hidden">
                    <button
                      type="button"
                      aria-pressed={selectedVisualization === "table"}
                      className={`flex items-center space-x-1 px-4 py-2 text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-violet-600 ${
                        selectedVisualization === "table"
                          ? "bg-slate-100 text-violet-700"
                          : "bg-white text-slate-900"
                      } border-r border-slate-200 hover:bg-slate-100`}
                      onClick={() => setSelectedVisualization("table")}
                    >
                      <div>
                        <TocIcon style={{ fontSize: "20px" }} />
                      </div>
                      <p>Table</p>
                    </button>
                    {!isVolumeSensitivity && (
                      <button
                        type="button"
                        aria-pressed={selectedVisualization === "bar_chart"}
                        className={`flex items-center space-x-1 px-4 py-2 text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-violet-600 ${
                          selectedVisualization === "bar_chart"
                            ? "bg-slate-100 text-violet-700"
                            : "bg-white text-slate-900"
                        } border-r border-slate-200 hover:bg-slate-100`}
                        onClick={() => setSelectedVisualization("bar_chart")}
                      >
                        <div>
                          <BarChartIcon style={{ fontSize: "20px" }} />
                        </div>
                        <p>Bar</p>
                      </button>
                    )}
                    {!isVolumeSensitivity && (
                      <button
                        type="button"
                        aria-pressed={selectedVisualization === "line_chart"}
                        className={`flex items-center space-x-1 px-4 py-2 text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-violet-600 ${
                          selectedVisualization === "line_chart"
                            ? "bg-slate-100 text-violet-700"
                            : "bg-white text-slate-900"
                        } hover:bg-slate-100`}
                        onClick={() => setSelectedVisualization("line_chart")}
                      >
                        <div>
                          <StackedLineChartIcon style={{ fontSize: "20px" }} />
                        </div>
                        <p>Line</p>
                      </button>
                    )}
                    {isVolumeSensitivity && (
                      <button
                        type="button"
                        aria-pressed={selectedVisualization === "scatter_chart"}
                        className={`flex items-center space-x-1 px-4 py-2 text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-violet-600 ${
                          selectedVisualization === "scatter_chart"
                            ? "bg-slate-100 text-violet-700"
                            : "bg-white text-slate-900"
                        } hover:bg-slate-100`}
                        onClick={() => setSelectedVisualization("scatter_chart")}
                      >
                        <div>
                          <AutoGraphIcon style={{ fontSize: "20px" }} />
                        </div>
                        <p>Scatter</p>
                      </button>
                    )}
                  </div>
                  {!isVolumeSensitivity && (
                    <button
                      type="button"
                      aria-pressed={selectedVisualization === "labor_standards"}
                      className={`flex items-center space-x-1 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-600 ${
                        selectedVisualization === "labor_standards"
                          ? "bg-slate-100 text-violet-700"
                          : "bg-white text-slate-900"
                      } hover:bg-slate-100`}
                      onClick={() => setSelectedVisualization("labor_standards")}
                    >
                      <div>
                        <AlignHorizontalLeftIcon style={{ fontSize: "20px" }} />
                      </div>
                      <p>Labor standards</p>
                    </button>
                  )}
                </div>
                <div className="flex items-center space-x-4">
                  <div className="w-60">
                    <FormControl
                      fullWidth
                      size="small"
                      sx={{
                        borderRadius: "10px",
                        backgroundColor: "white",
                      }}
                    >
                      <Select
                        labelId="demo-simple-select-label"
                        id="demo-simple-select"
                        value={selectedMetric}
                        onChange={handleSelectMetricChange}
                        sx={{
                          borderRadius: "10px",
                          fieldset: { borderColor: "#e2e8f0" },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#6d28d9",
                          },
                        }}
                      >
                        <MenuItem value={"hours"}>Hours</MenuItem>
                        <MenuItem value={"splh"}>SPLH</MenuItem>
                        <MenuItem value={"labor_percent"}>
                          Labor Percent
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </div>
                  <div className="w-60">
                    <FormControl
                      size="small"
                      fullWidth
                      sx={{
                        borderRadius: "10px",
                        backgroundColor: "white",
                      }}
                    >
                      <Select
                        fullWidth
                        labelId="demo-multiple-checkbox-label"
                        id="demo-multiple-checkbox"
                        multiple
                        value={selectedDepartments}
                        onChange={handleDepartmentSelectionChange}
                        input={<OutlinedInput />}
                        renderValue={(selected) => selected.join(", ")}
                        MenuProps={MenuProps}
                        sx={{
                          borderRadius: "10px",
                          fieldset: { borderColor: "#e2e8f0" },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#6d28d9",
                          },
                        }}
                      >
                        {departments.map((name) => (
                          <MenuItem
                            key={name}
                            value={name}
                            // classes={{ root: classes.menuItem }}
                          >
                            <Checkbox
                              checked={selectedDepartments.indexOf(name) > -1}
                              style={{ color: "#6d28d9" }}
                            />
                            <ListItemText primary={name} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </div>
                </div>
              </div>
              {selectedVisualization === "table" ? (
                isVolumeSensitivity ? (
                  <div className="mt-8 w-full space-y-6">
                    {volumeSensitivityData?.storeTables.map((store) => (
                      <div
                        key={store.storeName}
                        className="w-full bg-white border border-slate-200 rounded-lg overflow-hidden"
                      >
                        <div className="flex items-center border-b border-slate-200 py-2">
                          <div className="w-[18%] px-6 py-2">
                            <p className="text-slate-900 text-sm font-semibold">
                              {store.storeName}
                            </p>
                          </div>
                          <div className="w-[82%] flex gap-3">
                            {store.volumes.map((volume) => (
                              <div
                                key={volume}
                                className="flex-1 flex items-center"
                              >
                                <p className="text-slate-500 text-sm">
                                  {formatValue(volume, "$", 0)}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                        {xAxisLabels.map((day) => (
                          <div
                            key={day}
                            className="w-full flex border-b border-slate-200 py-3.5"
                          >
                            <div className="w-[18%] text-slate-900 px-6 py-2">
                              <p className="text-slate-900 text-sm">{day}</p>
                            </div>
                            <div className="w-[82%] flex gap-3">
                              {store.volumes.map((volume) => {
                                const entry =
                                  store.dayLookup?.[day]?.[volume];
                                const totals = calculateNewTotals(
                                  entry?.labor_results || [],
                                  selectedDepartments
                                );
                                const sales = entry?.sales || 0;
                                const displayValue =
                                  selectedMetric === "hours"
                                    ? formatValue(totals.new_hours)
                                    : selectedMetric === "splh"
                                    ? formatValue(
                                        totals.new_hours > 0
                                          ? sales / totals.new_hours
                                          : 0,
                                        "$"
                                      )
                                    : selectedMetric === "labor_percent"
                                    ? formatValue(
                                        sales > 0
                                          ? (totals.new_cost / sales) * 100
                                          : 0,
                                        "%"
                                      )
                                    : "";
                                return (
                                  <div
                                    key={`${day}-${volume}`}
                                    className="flex-1 rounded-lg px-3 py-2 border border-slate-100 bg-slate-50"
                                  >
                                    <p className="text-sm text-slate-900">
                                      {displayValue}
                                    </p>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                ) : (
                <div className="mt-8 w-full bg-white border border-slate-200 rounded-lg overflow-hidden">
                  <div className="flex items-center border-b border-slate-200 py-2">
                    <div className="w-[14%] py-2"></div>
                    <div className="w-[86%] flex gap-3">
                      <div className="flex-1 pr-10 text-white">
                        <p className="text-sm whitespace-nowrap">
                          {workingModelLabel}{" "}
                          {selectedMetric === "hours"
                            ? "Hours"
                            : selectedMetric === "splh"
                            ? "SPLH"
                            : selectedMetric === "labor_percent"
                            ? "Labor %"
                            : ""}
                        </p>
                        <p className="text-sm mt-2 whitespace-nowrap">
                          {runType === "forecasted_sandbox_vs_published"
                            ? "Published"
                            : "Actual"}{" "}
                          {selectedMetric === "hours"
                            ? "Hours"
                            : selectedMetric === "splh"
                            ? "SPLH"
                            : selectedMetric === "labor_percent"
                            ? "Labor %"
                            : ""}
                        </p>
                      </div>
                      {xAxisLabels.map((day, index) => (
                        <div key={index} className="flex-1 flex items-center">
                          <p className="text-slate-500 text-sm">{day}</p>
                        </div>
                      ))}
                      <div className="flex-1 flex items-center mr-3">
                        <p className="text-slate-900 font-bold text-sm">Total</p>
                      </div>
                    </div>
                  </div>
                  <p className="px-6 pt-3 text-xs text-slate-500">
                    Click any cell to view the
                    intraday comparison.
                  </p>
                  {data && calculationStatus === "completed" && (
                    <div>
                      {Object.entries(data).map(([location, values]) => {
                        let sumNewHours = 0;
                        let sumOldHours = 0;
                        let sumSales = 0;
                        let sumNewCost = 0;
                        let sumOldCost = 0;
                        let totalHoursChange = 0;
                        let totalSPLHChange = 0;
                        let totalLaborPercentChange = 0;
                        const storeSummary =
                          storeIntradaySummaries[location] || {};
                        const weeklyIntraday = storeSummary.weeklyAverage;
                        const weeklySelectionId = storeSummary.storeId
                          ? `store-weekly-${storeSummary.storeId}`
                          : `store-weekly-${String(location)}`;
                        const weeklyHasIntraday = intradayHasData(
                          weeklyIntraday
                        );
                        const weeklyIsSelected =
                          intradaySelection?.storeId === weeklySelectionId &&
                          intradaySelection?.dayName === "Weekly total";
                        const weeklyCellClasses = [
                          "flex-1",
                          "mr-3",
                          "rounded-lg",
                          "px-3",
                          "py-2",
                          "border",
                          "border-transparent",
                          weeklyHasIntraday
                            ? "cursor-pointer hover:bg-violet-50 transition-colors"
                            : "",
                          weeklyIsSelected
                            ? "ring-2 ring-violet-400 bg-violet-50"
                            : "",
                        ]
                          .filter(Boolean)
                          .join(" ");
                        return (
                          <div
                            key={location}
                            className="w-full flex border-b border-slate-200 py-3.5"
                          >
                            <div className="w-[14%] text-slate-900 px-6 py-2">
                              <p className="text-slate-900 text-sm">
                                {location}
                              </p>
                            </div>
                            <div className="w-[86%] flex gap-3">
                              <div className="flex-1 pr-10">
                                <p className="text-sm text-slate-500 py-2 whitespace-nowrap">
                                  {runType === "forecasted_sandbox_vs_published"
                                    ? "Published"
                                    : "Actual"}{" "}
                                  {selectedMetric === "hours"
                                    ? "Hours"
                                    : selectedMetric === "splh"
                                    ? "SPLH"
                                    : selectedMetric === "labor_percent"
                                    ? "Labor %"
                                    : ""}
                                </p>
                                <p className="text-sm text-violet-700 whitespace-nowrap">
                                  {workingModelLabel}{" "}
                                  {selectedMetric === "hours"
                                    ? "Hours"
                                    : selectedMetric === "splh"
                                    ? "SPLH"
                                    : selectedMetric === "labor_percent"
                                    ? "Labor %"
                                    : ""}
                                </p>
                              </div>
                              {values?.map((dayValue, index) => {
                                const laborResultsByDepartment = calculateSum(
                                  dayValue.labor_results,
                                  selectedFields,
                                  selectedDepartments
                                );
                                const hoursChange = (
                                  laborResultsByDepartment.new_hours -
                                  laborResultsByDepartment.old_hours
                                ).toFixed(2);
                                const SPLHChange = (
                                  dayValue.sales /
                                    laborResultsByDepartment.new_hours -
                                  dayValue.sales /
                                    laborResultsByDepartment.old_hours
                                ).toFixed(2);
                                const laborPercentChange = (
                                  (laborResultsByDepartment.new_cost /
                                    dayValue.sales) *
                                    100 -
                                  (laborResultsByDepartment.old_cost /
                                    dayValue.sales) *
                                    100
                                ).toFixed(2);
                                sumNewHours +=
                                  laborResultsByDepartment.new_hours;
                                sumOldHours +=
                                  laborResultsByDepartment.old_hours;
                                sumSales += dayValue.sales;
                                sumNewCost += laborResultsByDepartment.new_cost;
                                sumOldCost += laborResultsByDepartment.old_cost;
                                totalHoursChange = (
                                  sumNewHours - sumOldHours
                                ).toFixed(2);
                                totalSPLHChange = (
                                  sumSales / sumNewHours -
                                  sumSales / sumOldHours
                                ).toFixed(2);
                                totalLaborPercentChange = (
                                  (sumNewCost / sumSales) * 100 -
                                  (sumOldCost / sumSales) * 100
                                ).toFixed(2);

                                const intradayLabor = dayValue?.intraday_labor;
                                const hasIntradayData =
                                  intradayHasData(intradayLabor);
                                const isSelected =
                                  intradaySelection?.storeId ===
                                    dayValue.store_id &&
                                  intradaySelection?.dayName ===
                                    dayValue.day_name;

                                const cellClasses = [
                                  "flex-1",
                                  "rounded-lg",
                                  "px-3",
                                  "py-2",
                                  "border",
                                  "border-transparent",
                                  hasIntradayData
                                    ? "cursor-pointer hover:bg-violet-50 transition-colors"
                                    : "",
                                  isSelected ? "ring-2 ring-violet-400 bg-violet-50" : "",
                                ]
                                  .filter(Boolean)
                                  .join(" ");

                                return (
                                  <div
                                    key={index}
                                    className={cellClasses}
                                    onClick={() =>
                                      hasIntradayData &&
                                      handleIntradaySelection(
                                        location,
                                        dayValue.day_name,
                                        intradayLabor,
                                        dayValue.store_id
                                      )
                                    }
                                  >
                                    {selectedMetric === "hours" ? (
                                      <div>
                                        <p className="text-sm">
                                          {formatValue(
                                            laborResultsByDepartment.old_hours
                                          )}
                                        </p>
                                        <p className="text-sm text-violet-700 mt-2">
                                          {formatValue(
                                            laborResultsByDepartment.new_hours
                                          )}
                                        </p>
                                        <div className="w-fit mt-2">
                                          <div
                                            className={`${getLaborDeltaBadgeClass(
                                              hoursChange,
                                              "hours"
                                            )} flex items-center justify-center rounded px-1`}
                                          >
                                            <div>
                                              {hoursChange < 0 ? (
                                                <RemoveIcon
                                                  style={{
                                                    fontSize: "10px",
                                                    marginBottom: "2px",
                                                  }}
                                                />
                                              ) : hoursChange > 0 ? (
                                                <AddIcon
                                                  style={{
                                                    fontSize: "10px",
                                                    marginBottom: "2px",
                                                  }}
                                                />
                                              ) : (
                                                <div></div>
                                              )}
                                            </div>
                                            <p className="text-xs">
                                              {formatValue(
                                                Math.abs(hoursChange)
                                              )}
                                            </p>
                                          </div>
                                        </div>
                                      </div>
                                    ) : selectedMetric === "splh" ? (
                                      <div>
                                        <p className="text-sm">
                                          {formatValue(
                                            dayValue.sales /
                                              laborResultsByDepartment.old_hours,
                                            "$"
                                          )}
                                        </p>
                                        <p className="text-sm text-violet-700 mt-2">
                                          {formatValue(
                                            dayValue.sales /
                                              laborResultsByDepartment.new_hours,
                                            "$"
                                          )}
                                        </p>
                                        <div className="w-fit mt-2">
                                          <div
                                            className={`${getLaborDeltaBadgeClass(
                                              SPLHChange,
                                              "splh"
                                            )} flex items-center justify-center rounded px-1`}
                                          >
                                            <div>
                                              {SPLHChange < 0 ? (
                                                <RemoveIcon
                                                  style={{
                                                    fontSize: "10px",
                                                    marginBottom: "2px",
                                                  }}
                                                />
                                              ) : SPLHChange > 0 ? (
                                                <AddIcon
                                                  style={{
                                                    fontSize: "10px",
                                                    marginBottom: "2px",
                                                  }}
                                                />
                                              ) : (
                                                <div></div>
                                              )}
                                            </div>
                                            <p className="text-xs">
                                              {formatValue(
                                                Math.abs(SPLHChange),
                                                "$"
                                              )}
                                            </p>
                                          </div>
                                        </div>
                                      </div>
                                    ) : selectedMetric === "labor_percent" ? (
                                      <div>
                                        <p className="text-sm">
                                          {formatValue(
                                            (laborResultsByDepartment.old_cost /
                                              dayValue.sales) *
                                              100,
                                            "%"
                                          )}
                                        </p>
                                        <p className="text-sm text-violet-700 mt-2">
                                          {formatValue(
                                            (laborResultsByDepartment.new_cost /
                                              dayValue.sales) *
                                              100,
                                            "%"
                                          )}
                                        </p>
                                        <div className="w-fit mt-2">
                                          <div
                                            className={`${getLaborDeltaBadgeClass(
                                              laborPercentChange,
                                              "labor_percent"
                                            )} flex items-center justify-center rounded px-1`}
                                          >
                                            <div>
                                              {laborPercentChange < 0 ? (
                                                <RemoveIcon
                                                  style={{
                                                    fontSize: "10px",
                                                    marginBottom: "2px",
                                                  }}
                                                />
                                              ) : laborPercentChange > 0 ? (
                                                <AddIcon
                                                  style={{
                                                    fontSize: "10px",
                                                    marginBottom: "2px",
                                                  }}
                                                />
                                              ) : (
                                                <div></div>
                                              )}
                                            </div>
                                            <p className="text-xs">
                                              {formatValue(
                                                Math.abs(laborPercentChange)
                                              )}
                                            </p>
                                          </div>
                                        </div>
                                      </div>
                                    ) : (
                                      <p></p>
                                    )}
                                  </div>
                                );
                              })}
                              <div
                                className={weeklyCellClasses}
                                onClick={() =>
                                  weeklyHasIntraday &&
                                  handleIntradaySelection(
                                    location,
                                    "Weekly total",
                                    weeklyIntraday,
                                    weeklySelectionId
                                  )
                                }
                              >
                                <p className="text-sm font-bold">
                                  {selectedMetric === "hours"
                                    ? formatValue(sumOldHours)
                                    : selectedMetric === "splh"
                                    ? `${formatValue(
                                        sumSales / sumOldHours,
                                        "$"
                                      )}`
                                    : selectedMetric === "labor_percent"
                                    ? `${formatValue(
                                        (sumOldCost / sumSales) * 100,
                                        "%"
                                      )}`
                                    : 0}
                                </p>
                                <p className="text-sm font-bold text-violet-700 mt-2">
                                  {selectedMetric === "hours"
                                    ? formatValue(sumNewHours)
                                    : selectedMetric === "splh"
                                    ? `${formatValue(
                                        sumSales / sumNewHours,
                                        "$"
                                      )}`
                                    : selectedMetric === "labor_percent"
                                    ? `${formatValue(
                                        (sumNewCost / sumSales) * 100,
                                        "%"
                                      )}`
                                    : 0}
                                </p>
                                {selectedMetric === "hours" ? (
                                  <div className="w-fit mt-2">
                                    <div
                                      className={`${getLaborDeltaBadgeClass(
                                        totalHoursChange,
                                        "hours"
                                      )} flex items-center justify-center rounded px-1`}
                                    >
                                      <div>
                                        {totalHoursChange < 0 ? (
                                          <RemoveIcon
                                            style={{
                                              fontSize: "10px",
                                              marginBottom: "2px",
                                            }}
                                          />
                                        ) : totalHoursChange > 0 ? (
                                          <AddIcon
                                            style={{
                                              fontSize: "10px",
                                              marginBottom: "2px",
                                            }}
                                          />
                                        ) : (
                                          <div></div>
                                        )}
                                      </div>
                                      <p className="text-xs">
                                        {formatValue(
                                          Math.abs(totalHoursChange)
                                        )}
                                      </p>
                                    </div>
                                  </div>
                                ) : selectedMetric === "splh" ? (
                                  <div className="w-fit mt-2">
                                    <div
                                      className={`${getLaborDeltaBadgeClass(
                                        totalSPLHChange,
                                        "splh"
                                      )} flex items-center justify-center rounded px-1`}
                                    >
                                      <div>
                                        {totalSPLHChange < 0 ? (
                                          <RemoveIcon
                                            style={{
                                              fontSize: "10px",
                                              marginBottom: "2px",
                                            }}
                                          />
                                        ) : totalSPLHChange > 0 ? (
                                          <AddIcon
                                            style={{
                                              fontSize: "10px",
                                              marginBottom: "2px",
                                            }}
                                          />
                                        ) : (
                                          <div></div>
                                        )}
                                      </div>
                                      <p className="text-xs">
                                        {formatValue(
                                          Math.abs(totalSPLHChange),
                                          "$"
                                        )}
                                      </p>
                                    </div>
                                  </div>
                                ) : selectedMetric === "labor_percent" ? (
                                  <div className="w-fit mt-2">
                                    <div
                                      className={`${getLaborDeltaBadgeClass(
                                        totalLaborPercentChange,
                                        "labor_percent"
                                      )} flex items-center justify-center rounded px-1`}
                                    >
                                      <div>
                                        {totalLaborPercentChange < 0 ? (
                                          <RemoveIcon
                                            style={{
                                              fontSize: "10px",
                                              marginBottom: "2px",
                                            }}
                                          />
                                        ) : totalLaborPercentChange > 0 ? (
                                          <AddIcon
                                            style={{
                                              fontSize: "10px",
                                              marginBottom: "2px",
                                            }}
                                          />
                                        ) : (
                                          <div></div>
                                        )}
                                      </div>
                                      <p className="text-xs">
                                        {formatValue(
                                          Math.abs(totalLaborPercentChange)
                                        )}
                                      </p>
                                    </div>
                                  </div>
                                ) : (
                                  <div></div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {selectedMetric === "hours" ? (
                    <div className="w-full flex bg-slate-100 py-3.5">
                      <div className="w-[14%] text-slate-900 px-6 py-2">
                        <p className="text-slate-900 text-sm font-bold">
                          Average
                        </p>
                      </div>
                      <div className="w-[86%] flex gap-3">
                        <div className="flex-1 pr-10">
                          <p className="text-sm text-slate-500 py-2 whitespace-nowrap">
                            {runType === "forecasted_sandbox_vs_published"
                              ? "Published"
                              : "Actual"}{" "}
                            {selectedMetric === "hours"
                              ? "Hours"
                              : selectedMetric === "splh"
                              ? "SPLH"
                              : selectedMetric === "labor_percent"
                              ? "Labor %"
                              : ""}
                          </p>
                          <p className="text-sm text-violet-700 whitespace-nowrap">
                            {workingModelLabel}{" "}
                            {selectedMetric === "hours"
                              ? "Hours"
                            : selectedMetric === "splh"
                            ? "SPLH"
                            : selectedMetric === "labor_percent"
                            ? "Labor %"
                            : ""}
                        </p>
                      </div>
                      {locationSumsByDayOfWeekArray.map((day, index) => {
                        const dayLabel = xAxisLabels[index];
                        const selectionId = `average-${dayLabel.toLowerCase()}`;
                        const averageIntraday = averageIntradayByDay[index];
                        const hasAverageIntraday = intradayHasData(
                          averageIntraday
                        );
                        const isSelected =
                          intradaySelection?.storeId === selectionId &&
                          intradaySelection?.dayName === dayLabel;
                        const cellClasses = [
                          "flex-1",
                          "rounded-lg",
                          "px-3",
                          "py-2",
                          "border",
                          "border-transparent",
                          hasAverageIntraday
                            ? "cursor-pointer hover:bg-violet-50 transition-colors"
                            : "",
                          isSelected ? "ring-2 ring-violet-400 bg-violet-50" : "",
                        ]
                          .filter(Boolean)
                          .join(" ");
                        const hoursChange = (
                          (day.new_hours - day.old_hours) /
                          numberOfLocationsInTest
                        ).toFixed(2);
                        return (
                          <div
                            key={index}
                            className={cellClasses}
                            onClick={() =>
                              hasAverageIntraday &&
                              handleIntradaySelection(
                                "Average across stores",
                                dayLabel,
                                averageIntraday,
                                selectionId
                              )
                            }
                          >
                            <p className="text-slate-900 text-sm font-bold">
                              {formatValue(
                                day.old_hours / numberOfLocationsInTest
                              )}
                            </p>
                            <p className="text-violet-700 text-sm font-bold mt-2">
                              {formatValue(
                                day.new_hours / numberOfLocationsInTest
                              )}
                            </p>
                            <div className="w-fit mt-2">
                              <div
                                className={`${getLaborDeltaBadgeClass(
                                  hoursChange,
                                  "hours"
                                )} flex items-center justify-center rounded px-1`}
                              >
                                <div>
                                  {hoursChange < 0 ? (
                                    <RemoveIcon
                                      style={{
                                        fontSize: "10px",
                                        marginBottom: "2px",
                                      }}
                                    />
                                  ) : hoursChange > 0 ? (
                                    <AddIcon
                                      style={{
                                        fontSize: "10px",
                                        marginBottom: "2px",
                                      }}
                                    />
                                  ) : (
                                    <div></div>
                                  )}
                                </div>
                                <p className="text-xs">
                                  {formatValue(Math.abs(hoursChange))}
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      <div
                        className={[
                          "flex-1",
                          "mr-3",
                          "rounded-lg",
                          "px-3",
                          "py-2",
                          "border",
                          "border-transparent",
                          intradayHasData(averageWeeklyIntraday)
                            ? "cursor-pointer hover:bg-violet-50 transition-colors"
                            : "",
                          intradaySelection?.storeId === "average-weekly-total" &&
                          intradaySelection?.dayName === "Weekly total"
                            ? "ring-2 ring-violet-400 bg-violet-50"
                            : "",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                        onClick={() =>
                          intradayHasData(averageWeeklyIntraday) &&
                          handleIntradaySelection(
                            "Average across stores",
                            "Weekly total",
                            averageWeeklyIntraday,
                            "average-weekly-total"
                          )
                        }
                      >
                        <p className="text-slate-900 text-sm font-bold">
                          {formatValue(
                            sumWeekData(locationSumsByDayOfWeekArray)
                              .old_hours / numberOfLocationsInTest
                          )}
                        </p>
                        <p className="text-violet-700 text-sm font-bold mt-2">
                          {formatValue(
                            sumWeekData(locationSumsByDayOfWeekArray)
                              .new_hours / numberOfLocationsInTest
                          )}
                        </p>
                        <div className="w-fit mt-2">
                          {/* Define hoursChange variable */}
                          {(() => {
                            const hoursChange = (
                              (sumWeekData(locationSumsByDayOfWeekArray)
                                .new_hours -
                                sumWeekData(locationSumsByDayOfWeekArray)
                                  .old_hours) /
                              numberOfLocationsInTest
                            ).toFixed(2);

                            const cssClass = getLaborDeltaBadgeClass(
                              hoursChange,
                              "hours"
                            );

                            // Render the styled div based on hoursChange
                            return (
                              <div
                                className={`flex items-center justify-center rounded px-1 ${cssClass}`}
                              >
                                <div>
                                  {hoursChange < 0 ? (
                                    <RemoveIcon
                                      style={{
                                        fontSize: "10px",
                                        marginBottom: "2px",
                                      }}
                                    />
                                  ) : hoursChange > 0 ? (
                                    <AddIcon
                                      style={{
                                        fontSize: "10px",
                                        marginBottom: "2px",
                                      }}
                                    />
                                  ) : (
                                    <div></div>
                                  )}
                                </div>
                                <p className="text-xs">
                                  {formatValue(Math.abs(hoursChange))}
                                </p>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                    </div>
                  ) : selectedMetric === "splh" ? (
                    <div className="w-full flex bg-slate-100 py-3.5">
                      <div className="w-[14%] text-slate-900 px-6 py-2">
                        <p className="text-slate-900 text-sm font-bold">
                          Average
                        </p>
                      </div>
                      <div className="w-[86%] flex gap-3">
                        <div className="flex-1 pr-10">
                          <p className="text-sm text-slate-500 whitespace-nowrap">
                            {runType === "forecasted_sandbox_vs_published"
                              ? "Published"
                              : "Actual"}{" "}
                            {selectedMetric === "hours"
                              ? "Hours"
                              : selectedMetric === "splh"
                              ? "SPLH"
                              : selectedMetric === "labor_percent"
                              ? "Labor %"
                              : ""}
                          </p>
                          <p className="text-sm text-violet-700 mt-2 whitespace-nowrap">
                            {workingModelLabel}{" "}
                            {selectedMetric === "hours"
                              ? "Hours"
                              : selectedMetric === "splh"
                              ? "SPLH"
                              : selectedMetric === "labor_percent"
                              ? "Labor %"
                              : ""}
                          </p>
                        </div>
                      {locationSumsByDayOfWeekArray.map((day, index) => {
                        const dayLabel = xAxisLabels[index];
                        const selectionId = `average-${dayLabel.toLowerCase()}`;
                        const averageIntraday = averageIntradayByDay[index];
                        const hasAverageIntraday = intradayHasData(
                          averageIntraday
                        );
                        const isSelected =
                          intradaySelection?.storeId === selectionId &&
                          intradaySelection?.dayName === dayLabel;
                        const cellClasses = [
                          "flex-1",
                          "rounded-lg",
                          "px-3",
                          "py-2",
                          "border",
                          "border-transparent",
                          hasAverageIntraday
                            ? "cursor-pointer hover:bg-violet-50 transition-colors"
                            : "",
                          isSelected ? "ring-2 ring-violet-400 bg-violet-50" : "",
                        ]
                          .filter(Boolean)
                          .join(" ");
                        const SPLHChange = (
                          day.sales / day.new_hours -
                          day.sales / day.old_hours
                        ).toFixed(2);
                        return (
                          <div
                            key={index}
                            className={cellClasses}
                            onClick={() =>
                              hasAverageIntraday &&
                              handleIntradaySelection(
                                "Average across stores",
                                dayLabel,
                                averageIntraday,
                                selectionId
                              )
                            }
                          >
                            <p className="text-slate-900 text-sm font-bold">
                              {formatValue(day.sales / day.old_hours, "$")}
                            </p>
                            <p className="text-violet-700 text-sm font-bold mt-2">
                              {formatValue(day.sales / day.new_hours, "$")}
                            </p>
                            <div className="w-fit mt-2">
                              <div
                                className={`${getLaborDeltaBadgeClass(
                                  SPLHChange,
                                  "splh"
                                )} flex items-center justify-center rounded px-1`}
                              >
                                <div>
                                  {SPLHChange < 0 ? (
                                    <RemoveIcon
                                      style={{
                                        fontSize: "10px",
                                        marginBottom: "2px",
                                      }}
                                    />
                                  ) : SPLHChange > 0 ? (
                                    <AddIcon
                                      style={{
                                        fontSize: "10px",
                                        marginBottom: "2px",
                                      }}
                                    />
                                  ) : (
                                    <div></div>
                                  )}
                                </div>
                                <p className="text-xs">
                                  {formatValue(Math.abs(SPLHChange), "$")}
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      <div
                        className={[
                          "flex-1",
                          "mr-3",
                          "rounded-lg",
                          "px-3",
                          "py-2",
                          "border",
                          "border-transparent",
                          intradayHasData(averageWeeklyIntraday)
                            ? "cursor-pointer hover:bg-violet-50 transition-colors"
                            : "",
                          intradaySelection?.storeId === "average-weekly-total" &&
                          intradaySelection?.dayName === "Weekly total"
                            ? "ring-2 ring-violet-400 bg-violet-50"
                            : "",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                        onClick={() =>
                          intradayHasData(averageWeeklyIntraday) &&
                          handleIntradaySelection(
                            "Average across stores",
                            "Weekly total",
                            averageWeeklyIntraday,
                            "average-weekly-total"
                          )
                        }
                      >
                        <p className="text-slate-900 text-sm font-bold">
                          {formatValue(
                            sumWeekData(locationSumsByDayOfWeekArray).sales /
                              sumWeekData(locationSumsByDayOfWeekArray)
                                .old_hours,
                            "$"
                          )}
                        </p>
                        <p className="text-violet-700 text-sm font-bold mt-2">
                          {formatValue(
                            sumWeekData(locationSumsByDayOfWeekArray).sales /
                              sumWeekData(locationSumsByDayOfWeekArray)
                                .new_hours,
                            "$"
                          )}
                        </p>
                        <div className="w-fit mt-2">
                          {/* Define SPLHChange variable */}
                          {(() => {
                            const SPLHChange = (
                              sumWeekData(locationSumsByDayOfWeekArray).sales /
                                sumWeekData(locationSumsByDayOfWeekArray)
                                  .new_hours -
                              sumWeekData(locationSumsByDayOfWeekArray).sales /
                                sumWeekData(locationSumsByDayOfWeekArray)
                                  .old_hours
                            ).toFixed(2);

                            const cssClass = getLaborDeltaBadgeClass(
                              SPLHChange,
                              "splh"
                            );

                            // Render the styled div based on SPLHChange
                            return (
                              <div
                                className={`flex items-center justify-center rounded px-1 ${cssClass}`}
                              >
                                <div>
                                  {SPLHChange < 0 ? (
                                    <RemoveIcon
                                      style={{
                                        fontSize: "10px",
                                        marginBottom: "2px",
                                      }}
                                    />
                                  ) : SPLHChange > 0 ? (
                                    <AddIcon
                                      style={{
                                        fontSize: "10px",
                                        marginBottom: "2px",
                                      }}
                                    />
                                  ) : (
                                    <div></div>
                                  )}
                                </div>
                                <p className="text-xs">
                                  {formatValue(Math.abs(SPLHChange), "$")}
                                </p>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                    </div>
                  ) : selectedMetric === "labor_percent" ? (
                    <div className="w-full flex bg-slate-100 py-3.5">
                      <div className="w-[14%] text-slate-900 px-6 py-2">
                        <p className="text-slate-900 text-sm font-bold">
                          Average
                        </p>
                      </div>
                      <div className="w-[86%] flex gap-3">
                        <div className="flex-1 pr-10">
                          <p className="text-sm text-slate-500 whitespace-nowrap">
                            {runType === "forecasted_sandbox_vs_published"
                              ? "Published"
                              : "Actual"}{" "}
                            {selectedMetric === "hours"
                              ? "Hours"
                              : selectedMetric === "splh"
                              ? "SPLH"
                              : selectedMetric === "labor_percent"
                              ? "Labor %"
                              : ""}
                          </p>
                          <p className="text-sm text-violet-700 mt-2 whitespace-nowrap">
                            {workingModelLabel}{" "}
                            {selectedMetric === "hours"
                              ? "Hours"
                              : selectedMetric === "splh"
                              ? "SPLH"
                              : selectedMetric === "labor_percent"
                              ? "Labor %"
                              : ""}
                          </p>
                        </div>
                        {locationSumsByDayOfWeekArray.map((day, index) => {
                          const dayLabel = xAxisLabels[index];
                          const selectionId = `average-${dayLabel.toLowerCase()}`;
                          const averageIntraday = averageIntradayByDay[index];
                          const hasAverageIntraday = intradayHasData(
                            averageIntraday
                          );
                          const isSelected =
                            intradaySelection?.storeId === selectionId &&
                            intradaySelection?.dayName === dayLabel;
                          const cellClasses = [
                            "flex-1",
                            "rounded-lg",
                            "px-3",
                            "py-2",
                            "border",
                            "border-transparent",
                            hasAverageIntraday
                              ? "cursor-pointer hover:bg-violet-50 transition-colors"
                              : "",
                            isSelected ? "ring-2 ring-violet-400 bg-violet-50" : "",
                          ]
                            .filter(Boolean)
                            .join(" ");
                          const laborPercentChange = (
                            (day.new_cost / day.sales) * 100 -
                            (day.old_cost / day.sales) * 100
                          ).toFixed(2);
                          return (
                            <div
                              key={index}
                              className={cellClasses}
                              onClick={() =>
                                hasAverageIntraday &&
                                handleIntradaySelection(
                                  "Average across stores",
                                  dayLabel,
                                  averageIntraday,
                                  selectionId
                                )
                              }
                            >
                              <p className="text-slate-900 text-sm font-bold">
                                {formatValue(
                                  (day.old_cost / day.sales) * 100,
                                  "%"
                                )}
                              </p>
                              <p className="text-violet-700 text-sm font-bold mt-2">
                                {formatValue(
                                  (day.new_cost / day.sales) * 100,
                                  "%"
                                )}
                              </p>
                              <div className="w-fit mt-2">
                                <div
                                  className={`${getLaborDeltaBadgeClass(
                                    laborPercentChange,
                                    "labor_percent"
                                  )} flex items-center justify-center rounded px-1`}
                                >
                                  <div>
                                    {laborPercentChange < 0 ? (
                                      <RemoveIcon
                                        style={{
                                          fontSize: "10px",
                                          marginBottom: "2px",
                                        }}
                                      />
                                    ) : laborPercentChange > 0 ? (
                                      <AddIcon
                                        style={{
                                          fontSize: "10px",
                                          marginBottom: "2px",
                                        }}
                                      />
                                    ) : (
                                      <div></div>
                                    )}
                                  </div>
                                  <p className="text-xs">
                                    {formatValue(Math.abs(laborPercentChange))}
                                  </p>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                        <div
                          className={[
                            "flex-1",
                            "mr-3",
                            "rounded-lg",
                            "px-3",
                            "py-2",
                            "border",
                            "border-transparent",
                            intradayHasData(averageWeeklyIntraday)
                              ? "cursor-pointer hover:bg-violet-50 transition-colors"
                              : "",
                            intradaySelection?.storeId === "average-weekly-total" &&
                            intradaySelection?.dayName === "Weekly total"
                              ? "ring-2 ring-violet-400 bg-violet-50"
                              : "",
                          ]
                            .filter(Boolean)
                            .join(" ")}
                          onClick={() =>
                            intradayHasData(averageWeeklyIntraday) &&
                            handleIntradaySelection(
                              "Average across stores",
                              "Weekly total",
                              averageWeeklyIntraday,
                              "average-weekly-total"
                            )
                          }
                        >
                          <p className="text-slate-900 text-sm font-bold">
                            {formatValue(
                              (sumWeekData(locationSumsByDayOfWeekArray).old_cost /
                                sumWeekData(locationSumsByDayOfWeekArray).sales) *
                                100,
                              "%"
                            )}
                          </p>
                          <p className="text-violet-700 text-sm font-bold mt-2">
                            {formatValue(
                              (sumWeekData(locationSumsByDayOfWeekArray).new_cost /
                                sumWeekData(locationSumsByDayOfWeekArray).sales) *
                                100,
                              "%"
                            )}
                          </p>
                          <div className="w-fit mt-2">
                            {/* Define laborPercentChange variable */}
                            {(() => {
                              const laborPercentChange = (
                                (sumWeekData(locationSumsByDayOfWeekArray).new_cost /
                                  sumWeekData(locationSumsByDayOfWeekArray).sales) *
                                  100 -
                                (sumWeekData(locationSumsByDayOfWeekArray).old_cost /
                                  sumWeekData(locationSumsByDayOfWeekArray).sales) *
                                  100
                              ).toFixed(2);

                              const cssClass = getLaborDeltaBadgeClass(
                                laborPercentChange,
                                "labor_percent"
                              );

                              // Render the styled div based on laborPercentChange
                              return (
                                <div
                                  className={`flex items-center justify-center rounded px-1 ${cssClass}`}
                                >
                                  <div>
                                    {laborPercentChange < 0 ? (
                                      <RemoveIcon
                                        style={{
                                          fontSize: "10px",
                                          marginBottom: "2px",
                                        }}
                                      />
                                    ) : laborPercentChange > 0 ? (
                                      <AddIcon
                                        style={{
                                          fontSize: "10px",
                                          marginBottom: "2px",
                                        }}
                                      />
                                    ) : (
                                      <div></div>
                                    )}
                                  </div>
                                  <p className="text-xs">
                                    {formatValue(Math.abs(laborPercentChange))}
                                  </p>
                                </div>
                              );
                            })()}
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div></div>
                  )}
                </div>
              )) : selectedVisualization === "scatter_chart" ? (
                <div className="w-full flex flex-col items-center pr-20 pt-10">
                  <h3 className="text-lg ml-10">
                    Volume Sensitivity: Sales vs{" "}
                    {selectedMetric === "hours"
                      ? "Labor Hours"
                      : selectedMetric === "splh"
                      ? "SPLH"
                      : "Labor Percent"}
                  </h3>
                  <div className="mt-5 w-full flex justify-center">
                    <ScatterChart width={1000} height={420}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="sales"
                        name="Sales"
                        type="number"
                        domain={["dataMin", "dataMax"]}
                        tickFormatter={(value) =>
                          formatValue(value, "$", 0)
                        }
                      />
                      <YAxis
                        dataKey="value"
                        name="Metric"
                        type="number"
                        domain={["dataMin", "dataMax"]}
                        tickFormatter={(value) =>
                          selectedMetric === "labor_percent"
                            ? formatValue(value, "%")
                            : selectedMetric === "splh"
                            ? formatValue(value, "$")
                            : formatValue(value)
                        }
                      />
                      <Tooltip
                        formatter={(value, name, props) => [
                          selectedMetric === "labor_percent"
                            ? formatValue(value, "%")
                            : selectedMetric === "splh"
                            ? formatValue(value, "$")
                            : formatValue(value),
                          "Value",
                        ]}
                        labelFormatter={(label, payload) => {
                          const entry = payload?.[0]?.payload;
                          if (!entry) return "";
                          return `${entry.storeName} · ${entry.dayName}`;
                        }}
                      />
                      <Legend />
                      {volumeSensitivityData &&
                        xAxisLabels.map((day, index) => (
                          <Scatter
                            key={day}
                            name={day}
                            data={
                              volumeSensitivityData.scatterSeries?.[day] || []
                            }
                            fill={[
                              "#6d28d9",
                              "#9333ea",
                              "#a855f7",
                              "#c084fc",
                              "#60a5fa",
                              "#38bdf8",
                              "#2dd4bf",
                            ][index % 7]}
                          />
                        ))}
                    </ScatterChart>
                  </div>
                </div>
              ) : selectedVisualization === "bar_chart" ? (
                selectedMetric === "hours" ? (
                  <div className="w-full flex flex-col items-center pr-20 pt-10">
                    {selectedStores.length === 1 ? (
                      <h3 className="text-lg ml-10">
                        Labor Hours Recommended For {storeName}
                      </h3>
                    ) : (
                      <h3 className="text-lg ml-10">
                        Average Labor Hours Recommended Across{" "}
                        {selectedStores.length} Locations
                      </h3>
                    )}
                    <div className="mt-5">
                      <BarChart
                        width={1000}
                        height={400}
                        data={averageHoursArray}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend content={<CustomLegend />} />
                        <Bar dataKey="old_hours" fill="#64748b" />
                        <Bar dataKey="new_hours" fill="#6d28d9" />
                      </BarChart>
                    </div>
                  </div>
                ) : selectedMetric === "splh" ? (
                  <div className="w-full flex flex-col items-center pr-20 pt-10">
                    {selectedStores.length === 1 ? (
                      <h3 className="text-lg ml-10">
                        Sales Per Labor Hour Recommended For {storeName}
                      </h3>
                    ) : (
                      <h3 className="text-lg ml-10">
                        Average Sales Per Labor Hour Recommended Across{" "}
                        {selectedStores.length} Locations
                      </h3>
                    )}
                    <div className="mt-5">
                      <BarChart
                        width={1000}
                        height={400}
                        data={averageSPLHArray}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend content={<CustomLegend />} />
                        <Bar dataKey="old_SPLH" fill="#64748b" />
                        <Bar dataKey="new_SPLH" fill="#6d28d9" />
                      </BarChart>
                    </div>
                  </div>
                ) : selectedMetric === "labor_percent" ? (
                  <div className="w-full flex flex-col items-center pr-20 pt-10">
                    {selectedStores.length === 1 ? (
                      <h3 className="text-lg ml-10">
                        Labor Percent Recommended For {storeName}
                      </h3>
                    ) : (
                      <h3 className="text-lg ml-10">
                        Average Labor Percent Recommended Across{" "}
                        {selectedStores.length} Locations
                      </h3>
                    )}
                    <div className="mt-5">
                      <BarChart
                        width={1000}
                        height={400}
                        data={averageLaborPercentArray}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend content={<CustomLegend />} />
                        <Bar dataKey="old_labor_percent" fill="#64748b" />
                        <Bar dataKey="new_labor_percent" fill="#6d28d9" />
                      </BarChart>
                    </div>
                  </div>
                ) : (
                  <div></div>
                )
              ) : selectedVisualization === "line_chart" ? (
                selectedMetric === "hours" ? (
                  <div className="w-full flex flex-col items-center pr-20 pt-10">
                    {selectedStores.length === 1 ? (
                      <h3 className="text-lg ml-10">
                        Labor Hours Recommended For {storeName}
                      </h3>
                    ) : (
                      <h3 className="text-lg ml-10">
                        Average Labor Hours Recommended Across{" "}
                        {selectedStores.length} locations
                      </h3>
                    )}
                    <div className="mt-5">
                      <LineChart
                        width={1000}
                        height={400}
                        data={averageHoursArray}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend content={<CustomLegend />} />
                        <Line
                          type="monotone"
                          dataKey="new_hours"
                          stroke="#6d28d9"
                        />
                        <Line
                          type="monotone"
                          dataKey="old_hours"
                          stroke="#64748b"
                        />
                      </LineChart>
                    </div>
                  </div>
                ) : selectedMetric === "splh" ? (
                  <div className="w-full flex flex-col items-center pr-20 pt-10">
                    {selectedStores.length === 1 ? (
                      <h3 className="text-lg ml-10">
                        Sales Per Labor Hour Recommended For {storeName}
                      </h3>
                    ) : (
                      <h3 className="text-lg ml-10">
                        Average Sales Per Labor Hour Recommended Across{" "}
                        {selectedStores.length} Locations
                      </h3>
                    )}
                    <div className="mt-5">
                      <LineChart
                        width={1000}
                        height={400}
                        data={averageSPLHArray}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend content={<CustomLegend />} />
                        <Line
                          type="monotone"
                          dataKey="new_SPLH"
                          stroke="#6d28d9"
                        />
                        <Line
                          type="monotone"
                          dataKey="old_SPLH"
                          stroke="#64748b"
                        />
                      </LineChart>
                    </div>
                  </div>
                ) : selectedMetric === "labor_percent" ? (
                  <div className="w-full flex flex-col items-center pr-20 pt-10">
                    {selectedStores.length === 1 ? (
                      <h3 className="text-lg ml-10">
                        Labor Percent Recommended For {storeName}
                      </h3>
                    ) : (
                      <h3 className="text-lg ml-10">
                        Average Labor Percent Recommended Across{" "}
                        {selectedStores.length} Locations
                      </h3>
                    )}
                    <div className="mt-5">
                      <LineChart
                        width={1000}
                        height={400}
                        data={averageLaborPercentArray}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend content={<CustomLegend />} />
                        <Line
                          type="monotone"
                          dataKey="new_labor_percent"
                          stroke="#6d28d9"
                        />
                        <Line
                          type="monotone"
                          dataKey="old_labor_percent"
                          stroke="#64748b"
                        />
                      </LineChart>
                    </div>
                  </div>
              ) : ( 
                <div></div>
              )
            ) : selectedVisualization === "labor_standards" ? (
                selectedMetric === "hours" ? (
                  <div className="w-full flex flex-col items-center pr-10 pt-10">
                    <h3 className="text-lg ml-10">
                      {workingModelLabel} labor standards contribution to labor hours
                    </h3>
                    <div className="mt-5">
                      <BarChart
                        width={1250}
                        height={450}
                        data={laborStandardsDataByDepartment}
                        layout="vertical"
                        // margin={{ top: 20, right: 30, left: 150, bottom: 20 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          type="number"
                          domain={[0, laborStandardsMaxHours]}
                        />
                        <YAxis
                          type="category"
                          dataKey="name"
                          width={250}
                          interval={0}
                          tick={{ fontSize: 12 }}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend content={<CustomLegend />} />
                        <Bar dataKey="hours" fill="#6d28d9" />
                      </BarChart>
                    </div>
                  </div>
                ) : selectedMetric === "splh" ? (
                  <div className="w-full h-[420px] flex flex-col items-center justify-center pt-10">
                    <p className="text-xl text-slate-500">
                      Labor standards contribution to SPLH is not a relevant
                      metric
                    </p>
                    <p className="text-xl text-slate-500 mt-5">
                      Please select the metric "Hours" to see contributions
                    </p>
                  </div>
                ) : selectedMetric === "labor_percent" ? (
                  <div className="w-full h-[420px] flex flex-col items-center justify-center pt-10">
                    <p className="text-xl text-slate-500">
                      Labor standards contribution to labor percent is not a
                      relevant metric
                    </p>
                    <p className="text-xl text-slate-500 mt-5">
                      Please select the metric "Hours" to see contributions
                    </p>
                  </div>
                ) : (
                  <div></div>
                )
              ) : (
                <div></div>
              )}
              {intradaySelection && (
                <div className="mt-8 bg-white border border-slate-200 rounded-lg p-6">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                      <h3 className="text-lg font-medium text-slate-900">
                        Intraday labor for {intradaySelection.storeName} on
                        {" "}
                        {intradaySelection.dayName}
                      </h3>
                      {intradayDepartmentsLabel && (
                        <p className="text-sm text-slate-500 mt-1">
                          {intradayDepartmentsLabel}
                        </p>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => setIntradaySelection(null)}
                      className="self-start rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-600 hover:bg-slate-100"
                    >
                      Clear selection
                    </button>
                  </div>
                  {!hasActiveIntradayChartData ? (
                    <p className="text-sm text-slate-500 mt-6">
                      No intraday labor data available for the selected store
                      and day.
                    </p>
                  ) : isForecastRun ? (
                    <div className="mt-6">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                          <h4 className="text-sm font-semibold text-slate-900">
                            Forecasted labor target
                          </h4>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5">
                            {[
                              { key: "published", label: "Published" },
                              { key: "sandbox", label: workingModelLabel },
                            ].map((option) => {
                              const isActive =
                                intradayForecastTargetMode === option.key;
                              return (
                                <button
                                  key={option.key}
                                  type="button"
                                  onClick={() =>
                                    setIntradayForecastTargetMode(option.key)
                                  }
                                  className={`rounded-md px-3 py-1.5 text-sm transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-violet-500 ${
                                    isActive
                                      ? "bg-violet-50 text-violet-700 shadow-sm"
                                      : "text-slate-600 hover:bg-slate-50"
                                  }`}
                                >
                                  {option.label}
                                </button>
                              );
                            })}
                          </div>
                          <label className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-600">
                            <input
                              type="checkbox"
                              checked={showIntradayScheduledHours}
                              onChange={(event) =>
                                setShowIntradayScheduledHours(
                                  event.target.checked
                                )
                              }
                              className="h-4 w-4 accent-violet-600"
                            />
                            Scheduled labor
                          </label>
                        </div>
                      </div>
                      {renderForecastTargetLegend(showIntradayScheduledHours)}
                      <div className="mt-4 h-80">
                        {renderForecastIntradayChart(
                          forecastIntradayComparisonData,
                          forecastIntradayXAxisTicks,
                          showIntradayScheduledHours
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="mt-6">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                          <h4 className="text-sm font-semibold text-slate-900">
                            Earned labor vs actual labor
                          </h4>
                        </div>
                      </div>
                      {renderComponentLegend("Actual Labor")}
                      <div className="mt-4 h-80">
                        {renderComponentIntradayChart(
                          intradayActualBreakdownData,
                          intradayXAxisTicks,
                          "actual_hours",
                          "Actual Labor"
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </Box>
      </Modal>
    </div>
  );
}

export default LaborModelTestModal;
